import {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';

import KeyboardArrowDownRoundedIcon
  from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon
  from '@mui/icons-material/KeyboardArrowRightRounded';

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  getMarginCenterCustomerContext,
  getMarginCenterCustomerProductContext,
  getMarginCenterCustomerProductHistory,
  getMarginCenterFollowUpInventory,
  getMarginCenterProductContext,
} from '../services/marginCenterService';

import {
  executePricingDecision,
} from '../services/pricingDecisionService';

import CustomerRelationshipActivityPanel
  from './CustomerRelationshipActivityPanel';

import CustomerRelationshipEconomicReadings
  from './CustomerRelationshipEconomicReadings';

import CustomerRelationshipBenchmarkPanel
  from './CustomerRelationshipBenchmarkPanel';

import RelationshipCommercialQueryPanel
  from './RelationshipCommercialQueryPanel';

import CustomerRelationshipPositionPanel
  from './CustomerRelationshipPositionPanel';

import CustomerRelationshipFollowUpPanel
  from './CustomerRelationshipFollowUpPanel';

import MarginCenterTemporalContextBar
  from './MarginCenterTemporalContextBar';

import PricingDecisionContextEvidencePanel
  from './PricingDecisionContextEvidencePanel';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerContextParams,
  MarginCenterCustomerProductContextParams,
  MarginCenterCustomerProductHistorySeriesPoint,
  MarginCenterCustomerProductRecentTransaction,
  MarginCenterCustomerProductSelection,
  MarginCenterCustomerProductSeriesPoint,
  MarginCenterHistoricalTimeGrain,
  MarginCenterProductContextParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';

import type {
  PricingDecisionContextEvidence,
  PricingDecisionStrategyProfile,
} from '../types/pricingDecision';


type WorkspaceMode =
  | 'analysis'
  | 'optimization'
  | 'follow_up';

type TimelineMode =
  | 'analysis'
  | 'history';

type RecentPeriodFilter = 'all' | 'current' | 'comparison' | 'history';
type RecentPeriodGroup = Exclude<RecentPeriodFilter, 'all'>;

const RECENT_OPERATIONS_LIMIT = 20;
const RECENT_PERIOD_LABELS: Record<RecentPeriodFilter, string> = {
  all: 'Todas',
  current: 'Actual',
  comparison: 'Comparado',
  history: 'Otro histórico',
};

type ChartMetric =
  | 'revenue'
  | 'units'
  | 'average_net_unit_price'
  | 'average_unit_cost'
  | 'margin_percentage';

type RelationshipChartPoint = {
  bucketIndex: number;
  label: string;
  current: number | null;
  comparison: number | null;
  historical: number | null;
};


const CHART_METRICS: Array<{
  value: ChartMetric;
  label: string;
}> = [
  { value: 'revenue', label: 'Ingresos' },
  { value: 'units', label: 'Cantidad' },
  { value: 'average_net_unit_price', label: 'Precio neto' },
  { value: 'average_unit_cost', label: 'Coste unitario' },
  { value: 'margin_percentage', label: 'Margen %' },
];


const STRATEGY_PRESETS: Array<{
  value: Exclude<
    PricingDecisionStrategyProfile,
    'manual'
  >;
  label: string;
  sensitivity: number;
  description: string;
}> = [
  {
    value: 'conservador',
    label: 'Conservadora',
    sensitivity: 25,
    description: 'Mayor peso de la inercia comercial.',
  },
  {
    value: 'equilibrado',
    label: 'Equilibrada',
    sensitivity: 50,
    description: 'Balance entre inercia y señal del modelo.',
  },
  {
    value: 'agresivo',
    label: 'Agresiva',
    sensitivity: 90,
    description: 'Mayor peso del escenario IA.',
  },
];

const DEFAULT_STRATEGY_PROFILE:
  PricingDecisionStrategyProfile = 'equilibrado';

const DEFAULT_STRATEGY_SENSITIVITY = 50;


const DISPLAY_CURRENCY = 'EUR';

const numberFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

const currencyFormatter = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: DISPLAY_CURRENCY,
  currencyDisplay: 'symbol',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return numberFormatter.format(value);
}

function formatCurrency(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return currencyFormatter.format(value);
}



function formatPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${numberFormatter.format(value)} %`;
}



function formatChartMetricValue(
  value: number,
  metric: ChartMetric,
): string {
  if (!Number.isFinite(value)) return '—';

  if (
    metric === 'revenue'
    || metric === 'average_net_unit_price'
    || metric === 'average_unit_cost'
  ) {
    return formatCurrency(value);
  }

  if (metric === 'margin_percentage') {
    return formatPct(value);
  }

  return formatNumber(value);
}

function formatSignedPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} %`;
}


function calculateRelativeDeltaPct(
  referenceValue: number | null | undefined,
  candidateValue: number | null | undefined,
): number | null {
  if (
    referenceValue === null
    || referenceValue === undefined
    || candidateValue === null
    || candidateValue === undefined
    || referenceValue === 0
  ) {
    return null;
  }

  return (
    (
      candidateValue - referenceValue
    )
    / referenceValue
  ) * 100;
}


function getStrategyProfileLabel(
  profile:
    | PricingDecisionStrategyProfile
    | string
    | null
    | undefined,
): string {
  switch (profile) {
    case 'conservador':
      return 'Conservadora';

    case 'equilibrado':
      return 'Equilibrada';

    case 'agresivo':
      return 'Agresiva';

    case 'manual':
      return 'Manual';

    default:
      return '—';
  }
}


function getRiskLabel(
  value: string | null | undefined,
): string {
  switch (value) {
    case 'low':
      return 'Bajo';

    case 'medium':
      return 'Medio';

    case 'high':
      return 'Alto';

    default:
      return '—';
  }
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) return '—';

  return `${value > 0 ? '+' : ''}${numberFormatter.format(value)} p.p.`;
}


function formatDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const normalized = value.slice(0, 10);
  const [year, month, day] = normalized.split('-').map(Number);

  if (!year || !month || !day) return value;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}


// Fechas civiles (YYYY-MM-DD) tal como las expone el contexto seleccionado.
// Si una comparación personalizada se solapa, el periodo actual tiene prioridad
// visual; una transacción nunca se duplica entre filtros.
function classifyRecentTransaction(
  transaction: MarginCenterCustomerProductRecentTransaction,
  selection: MarginCenterCustomerProductSelection,
): RecentPeriodGroup {
  const day = transaction.transaction_date.slice(0, 10);
  if (
    selection.start_date && selection.end_date
    && day >= selection.start_date && day <= selection.end_date
  ) return 'current';
  if (
    selection.comparison_mode !== 'none'
    && selection.comparison_start_date && selection.comparison_end_date
    && day >= selection.comparison_start_date
    && day <= selection.comparison_end_date
  ) return 'comparison';
  return 'history';
}

function formatShortDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const normalized = value.slice(0, 10);
  const [year, month, day] = normalized.split('-').map(Number);

  if (!year || !month || !day) return normalized;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
  }).format(new Date(year, month - 1, day));
}

function formatHistoricalBucketLabel(
  value: string | null | undefined,
  timeGrain: MarginCenterTimeGrain | null | undefined,
): string {
  if (!value) return '—';

  const normalized = value.slice(0, 10);
  const [year, month, day] =
    normalized.split('-').map(Number);

  if (!year || !month || !day) {
    return normalized;
  }

  const date = new Date(year, month - 1, day);

  if (timeGrain === 'month') {
    return new Intl.DateTimeFormat('es-ES', {
      month: 'short',
      year: '2-digit',
    }).format(date);
  }

  if (timeGrain === 'week') {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    }).format(date);
  }

  return formatShortDate(value);
}


function getHistoricalTimeGrainLabel(
  timeGrain: MarginCenterTimeGrain | null | undefined,
): string {
  switch (timeGrain) {
    case 'day':
      return 'día';

    case 'week':
      return 'semana';

    case 'month':
      return 'mes';

    default:
      return '—';
  }
}

function CompactMetric({
  label,
  value,
  comparison,
  change,
  changeValue,
}: {
  label: string;
  value: string;
  comparison?: string;
  change?: string;
  changeValue?: number | null;
}) {
  const changeColor =
    changeValue === null
    || changeValue === undefined
    || changeValue === 0
      ? '#94a3b8'
      : changeValue > 0
        ? '#34d399'
        : '#fb7185';

  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.5,
        py: 1.2,
        borderRight: {
          xs: 'none',
          md: '1px solid rgba(148,163,184,0.12)',
        },
        '&:last-of-type': {
          borderRight: 'none',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#94a3b8',
          fontWeight: 700,
          minHeight: comparison !== undefined ? '2.4em' : undefined,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h6"
        sx={{
          mt: 0.2,
          color: '#f8fafc',
          fontWeight: 850,
          fontSize: '1.36rem',
          lineHeight: 1.25,
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>

      {comparison !== undefined && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.65,
            color: '#718198',
            fontSize: '0.78rem',
            lineHeight: 1.35,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {comparison}
        </Typography>
      )}

      {change !== undefined && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.28,
            color: changeColor,
            fontWeight: 850,
            fontSize: '0.91rem',
            lineHeight: 1.35,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {change}
        </Typography>
      )}
    </Box>
  );
}


function ChangeMetric({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 0.1,
          color: '#cbd5e1',
          fontWeight: 800,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}


function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Typography
        variant="body2"
        sx={{ color: '#94a3b8' }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: '#f8fafc',
          fontWeight: 750,
          textAlign: 'right',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}


function DecisionScenarioCard({
  label,
  price,
  marginPct,
  markupPct,
  cost,
  costLabel,
  deltaVsObservedPct,
  details = [],
  note,
  emphasized = false,
}: {
  label: string;
  price: number | null | undefined;
  marginPct: number | null | undefined;
  markupPct: number | null | undefined;
  cost: number | null | undefined;
  costLabel: string;
  deltaVsObservedPct: number | null | undefined;
  details?: Array<{
    label: string;
    value: string;
  }>;
  note?: string;
  emphasized?: boolean;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: 2,
        border: emphasized
          ? '1px solid rgba(56,189,248,0.34)'
          : '1px solid rgba(148,163,184,0.14)',
        bgcolor: emphasized
          ? 'rgba(14,165,233,0.055)'
          : 'rgba(15,23,42,0.34)',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: emphasized
            ? '#7dd3fc'
            : '#94a3b8',
          fontWeight: 850,
          letterSpacing: 0.75,
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h5"
        sx={{
          mt: 0.15,
          color: '#f8fafc',
          fontWeight: 900,
          letterSpacing: '-0.02em',
        }}
      >
        {formatCurrency(price)}
      </Typography>

      <Stack
        spacing={0.65}
        sx={{ mt: 1.35 }}
      >
        <DetailRow
          label="Margen s/ingresos"
          value={formatPct(marginPct)}
        />

        <DetailRow
          label="Markup s/coste"
          value={formatPct(markupPct)}
        />

        <DetailRow
          label={costLabel}
          value={formatCurrency(cost)}
        />

        <DetailRow
          label="Δ vs precio observado"
          value={formatSignedPct(
            deltaVsObservedPct,
          )}
        />

        {details.map((detail) => (
          <DetailRow
            key={detail.label}
            label={detail.label}
            value={detail.value}
          />
        ))}
      </Stack>

      {note && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.2,
            pt: 1,
            color: '#64748b',
            lineHeight: 1.45,
            borderTop:
              '1px solid rgba(148,163,184,0.10)',
          }}
        >
          {note}
        </Typography>
      )}
    </Paper>
  );
}

function ObservedDecisionStrip({
  price,
  marginPct,
  cost,
  units,
  revenue,
  coveragePct,
  recentTransactionsCount,
  recentTransactionsOpen,
  onToggleRecentTransactions,
}: {
  price: number | null | undefined;
  marginPct: number | null | undefined;
  cost: number | null | undefined;
  units: number | null | undefined;
  revenue: number | null | undefined;
  coveragePct: number | null | undefined;
  recentTransactionsCount: number;
  recentTransactionsOpen: boolean;
  onToggleRecentTransactions: () => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        px: {
          xs: 1.4,
          md: 1.6,
        },
        py: 1.2,
        borderRadius: 2,
        border:
          '1px solid rgba(148,163,184,0.14)',
        bgcolor:
          'rgba(15,23,42,0.26)',
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={0.75}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: '#94a3b8',
            fontWeight: 850,
            letterSpacing: 0.7,
          }}
        >
          Así veníamos trabajando
        </Typography>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Chip
            size="small"
            label={
              `Cobertura coste · ${formatPct(
                coveragePct,
              )}`
            }
            variant="outlined"
            sx={{
              height: 22,
              color: '#64748b',
              borderColor:
                'rgba(148,163,184,0.16)',
              '& .MuiChip-label': {
                px: 0.8,
                fontSize: 10.5,
                fontWeight: 750,
              },
            }}
          />

          <Button
            size="small"
            variant="text"
            disabled={
              recentTransactionsCount === 0
            }
            onClick={onToggleRecentTransactions}
            endIcon={
              recentTransactionsOpen
                ? (
                    <KeyboardArrowDownRoundedIcon
                      fontSize="small"
                    />
                  )
                : (
                    <KeyboardArrowRightRoundedIcon
                      fontSize="small"
                    />
                  )
            }
            sx={{
              minHeight: 22,
              px: 0.8,
              py: 0.1,
              color: '#7dd3fc',
              textTransform: 'none',
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {recentTransactionsOpen
              ? 'Ocultar operaciones'
              : `Ver últimas operaciones (${recentTransactionsCount})`}
          </Button>
        </Stack>
      </Stack>

      <Box
        sx={{
          mt: 0.75,
          display: 'grid',
          gridTemplateColumns: {
            xs:
              'repeat(2, minmax(0, 1fr))',
            md:
              'repeat(5, minmax(0, 1fr))',
          },
          gap: {
            xs: 1,
            md: 1.5,
          },
        }}
      >
        <ChangeMetric
          label="Precio medio"
          value={formatCurrency(price)}
        />

        <ChangeMetric
          label="Margen s/ingresos"
          value={formatPct(marginPct)}
        />

        <ChangeMetric
          label="Coste medio"
          value={formatCurrency(cost)}
        />

        <ChangeMetric
          label="Cantidad"
          value={formatNumber(units)}
        />

        <ChangeMetric
          label="Ingresos"
          value={formatCurrency(revenue)}
        />
      </Box>
    </Paper>
  );
}

function RecentTransactionsDecisionPanel({
  transactions,
}: {
  transactions:
    MarginCenterCustomerProductRecentTransaction[];
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        borderRadius: 2,
        border:
          '1px solid rgba(56,189,248,0.16)',
        bgcolor:
          'rgba(8,17,31,0.66)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1.1,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: '#f8fafc',
              fontWeight: 850,
            }}
          >
            Últimas operaciones
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
            }}
          >
            {transactions.length} operaciones
          </Typography>
        </Stack>
      </Box>

      <Divider />

      {transactions.length === 0 ? (
        <Box
          sx={{
            px: 2,
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
            }}
          >
            No hay operaciones recientes disponibles.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            maxHeight: 330,
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 760,
              '& .MuiTableCell-root': {
                py: 0.85,
                borderColor:
                  'rgba(148,163,184,0.09)',
                color: '#cbd5e1',
              },
              '& .MuiTableCell-head': {
                bgcolor: '#0b1525',
                color: '#64748b',
                fontSize: 11,
                fontWeight: 800,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  Fecha
                </TableCell>

                <TableCell align="right">
                  Cantidad
                </TableCell>

                <TableCell align="right">
                  Precio neto
                </TableCell>

                <TableCell align="right">
                  Margen s/ingresos
                </TableCell>

                <TableCell align="right">
                  Ingresos
                </TableCell>

                <TableCell align="right">
                  Coste
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {transactions.map(
                (transaction) => (
                  <TableRow
                    key={
                      transaction.transaction_id
                    }
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor:
                          'rgba(56,189,248,0.035)',
                      },
                    }}
                  >
                    <TableCell>
                      {formatDate(
                        transaction.transaction_date,
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {formatNumber(
                        transaction.quantity,
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {formatCurrency(
                        transaction.net_unit_price,
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {formatPct(
                        transaction.margin_percentage,
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {formatCurrency(
                        transaction.revenue,
                      )}
                    </TableCell>

                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={
                          transaction.cost_resolved
                            ? 'Resuelto'
                            : 'No resuelto'
                        }
                        variant="outlined"
                        sx={{
                          height: 20,
                          color: '#94a3b8',
                          borderColor:
                            'rgba(148,163,184,0.18)',
                          '& .MuiChip-label': {
                            px: 0.7,
                            fontSize: 10,
                            fontWeight: 750,
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function RecommendationDecisionCard({
  price,
  marginPct,
  markupPct,
  cost,
  deltaVsObservedPct,
  haircutVsMaxAiPct,
}: {
  price: number | null | undefined;
  marginPct: number | null | undefined;
  markupPct: number | null | undefined;
  cost: number | null | undefined;
  deltaVsObservedPct: number | null | undefined;
  haircutVsMaxAiPct: number | null | undefined;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: {
          xs: 1.5,
          md: 1.75,
        },
        borderRadius: 2,
        border:
          '1px solid rgba(56,189,248,0.38)',
        background:
          'linear-gradient(135deg, rgba(14,165,233,0.11) 0%, rgba(15,23,42,0.48) 100%)',
        boxShadow:
          '0 12px 30px rgba(2,6,23,0.18)',
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: '#7dd3fc',
          fontWeight: 900,
          letterSpacing: 0.8,
        }}
      >
        Recomendación
      </Typography>

      <Box
        sx={{
          mt: 0.65,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm:
              'repeat(2, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontWeight: 750,
            }}
          >
            Precio recomendado
          </Typography>

          <Typography
            sx={{
              mt: 0.1,
              color: '#f8fafc',
              fontSize: {
                xs: 28,
                md: 34,
              },
              lineHeight: 1.1,
              fontWeight: 950,
              letterSpacing: '-0.03em',
            }}
          >
            {formatCurrency(price)}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontWeight: 750,
            }}
          >
            Margen sobre ingresos recomendado
          </Typography>

          <Typography
            sx={{
              mt: 0.1,
              color: '#f8fafc',
              fontSize: {
                xs: 28,
                md: 34,
              },
              lineHeight: 1.1,
              fontWeight: 950,
              letterSpacing: '-0.03em',
            }}
          >
            {formatPct(marginPct)}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1.4 }} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs:
              'repeat(2, minmax(0, 1fr))',
            lg:
              'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.2,
        }}
      >
        <ChangeMetric
          label="Coste utilizado"
          value={formatCurrency(cost)}
        />

        <ChangeMetric
          label="Markup s/coste"
          value={formatPct(markupPct)}
        />

        <ChangeMetric
          label="Δ precio vs observado"
          value={formatSignedPct(
            deltaVsObservedPct,
          )}
        />

        <ChangeMetric
          label="Recorte vs máximo IA"
          value={formatPct(
            haircutVsMaxAiPct,
          )}
        />
      </Box>
    </Paper>
  );
}


function DecisionConfidenceScale({
  value,
}: {
  value: string | null | undefined;
}) {
  const normalized =
    (value ?? '').trim().toUpperCase();

  const position =
    normalized === 'BAJA'
      || normalized === 'LOW'
      ? 16.67
      : normalized === 'MEDIA'
        || normalized === 'MEDIUM'
        ? 50
        : normalized === 'ALTA'
          || normalized === 'HIGH'
          ? 83.33
          : null;

  const displayLabel =
    position === null
      ? 'No disponible'
      : (
          position < 30
            ? 'BAJA'
            : position < 70
              ? 'MEDIA'
              : 'ALTA'
        );

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
          }}
        >
          Confianza de la recomendación
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          {displayLabel}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mt: 1.25,
          height: 6,
          borderRadius: 999,
          bgcolor:
            'rgba(148,163,184,0.18)',
        }}
      >
        {position !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${position}%`,
              top: '50%',
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#38bdf8',
              border:
                '2px solid rgba(15,23,42,0.95)',
              transform:
                'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 0.7,
          display: 'grid',
          gridTemplateColumns:
            'repeat(3, minmax(0, 1fr))',
        }}
      >
        {['Baja', 'Media', 'Alta'].map(
          (label, index) => (
            <Typography
              key={label}
              variant="caption"
              sx={{
                color: '#64748b',
                textAlign:
                  index === 0
                    ? 'left'
                    : index === 1
                      ? 'center'
                      : 'right',
              }}
            >
              {label}
            </Typography>
          ),
        )}
      </Box>
    </Box>
  );
}


function DecisionRiskScale({
  score,
  label,
}: {
  score: number | null | undefined;
  label: string | null | undefined;
}) {
  const boundedScore =
    score === null || score === undefined
      ? null
      : Math.min(
          1,
          Math.max(0, score),
        );

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="baseline"
      >
        <Typography
          variant="caption"
          sx={{
            color: '#94a3b8',
            fontWeight: 800,
          }}
        >
          Riesgo operativo
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          {getRiskLabel(label)}
          {boundedScore !== null
            ? ` · ${numberFormatter.format(
                boundedScore,
              )} / 1,00`
            : ''}
        </Typography>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          mt: 1.25,
          height: 6,
          borderRadius: 999,
          bgcolor:
            'rgba(148,163,184,0.18)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '45%',
            top: -2,
            bottom: -2,
            width: '1px',
            bgcolor:
              'rgba(148,163,184,0.42)',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            left: '75%',
            top: -2,
            bottom: -2,
            width: '1px',
            bgcolor:
              'rgba(148,163,184,0.42)',
          }}
        />

        {boundedScore !== null && (
          <Box
            sx={{
              position: 'absolute',
              left:
                `${boundedScore * 100}%`,
              top: '50%',
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#38bdf8',
              border:
                '2px solid rgba(15,23,42,0.95)',
              transform:
                'translate(-50%, -50%)',
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          mt: 0.65,
          display: 'grid',
          gridTemplateColumns:
            '45% 30% 25%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'left',
          }}
        >
          Bajo
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'center',
          }}
        >
          Medio
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#64748b',
            textAlign: 'right',
          }}
        >
          Alto
        </Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          mt: 0.2,
          height: 16,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            left: '45%',
            color: '#475569',
            transform:
              'translateX(-50%)',
          }}
        >
          0,45
        </Typography>

        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            left: '75%',
            color: '#475569',
            transform:
              'translateX(-50%)',
          }}
        >
          0,75
        </Typography>
      </Box>
    </Box>
  );
}

function getSeriesValue(
  point:
    | MarginCenterCustomerProductSeriesPoint
    | MarginCenterCustomerProductHistorySeriesPoint,
  metric: ChartMetric,
): number | null {
  return point[metric];
}


export default function CustomerRelationshipWorkspace({
  customerId,
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  timeGrain,
  initialMode = 'analysis',
  initialFollowUpId,
  cadenceAttentionOccurrenceDate,
  onFollowUpIdChange,
  onClose,
}: {
  customerId: string;
  productId: string | null;
  startDate: string | null;
  endDate: string | null;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string | null;
  comparisonEndDate: string | null;
  timeGrain: MarginCenterTimeGrain;
  initialMode?: WorkspaceMode;
  initialFollowUpId?: string | null;
  cadenceAttentionOccurrenceDate?: string | null;
  onFollowUpIdChange?: (id: string) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [chartMetric, setChartMetric] =
    useState<ChartMetric>('revenue');

  const [timelineMode, setTimelineMode] =
    useState<TimelineMode>('analysis');

  const [workspaceMode, setWorkspaceMode] =
    useState<WorkspaceMode>(initialFollowUpId ? 'follow_up' : initialMode);

  // Keep the selected case when returning to analysis, including callers
  // without a URL callback (Explorer and contextual entry points).
  const [workspaceFollowUpId, setWorkspaceFollowUpId] =
    useState<string | null>(initialFollowUpId ?? null);

  useEffect(() => {
    setWorkspaceFollowUpId(initialFollowUpId ?? null);
  }, [customerId, productId, initialFollowUpId]);

  const [
    recentTransactionsOpen,
    setRecentTransactionsOpen,
  ] = useState(false);

  const [
    strategyProfile,
    setStrategyProfile,
  ] = useState<PricingDecisionStrategyProfile>(
    DEFAULT_STRATEGY_PROFILE,
  );

  const [
    strategySensitivity,
    setStrategySensitivity,
  ] = useState<number>(
    DEFAULT_STRATEGY_SENSITIVITY,
  );

  const [
    quantityInput,
    setQuantityInput,
  ] = useState('');

  const quantityValue =
    Number(
      quantityInput
        .trim()
        .replace(',', '.'),
    );

  const parsedQuantity =
    quantityInput.trim()
    && Number.isFinite(quantityValue)
    && quantityValue > 0
      ? quantityValue
      : null;

  const optimizationMutation = useMutation({
    mutationFn: () => {
      if (!productId) {
        throw new Error(
          'No existe un contexto cliente-producto seleccionado.',
        );
      }

      return executePricingDecision({
        version: '1.0',
        source: 'relationship',

        customer: {
          state: 'existing',
          customer_id: customerId,
          attributes: {},
        },

        product: {
          state: 'existing',
          product_id: productId,
          attributes: {},
          standard_cost: null,
        },

        commercial_inputs: {
          quantity: parsedQuantity,
          generate_pricing_matrix: true,
          strategy_profile: strategyProfile,
          strategy_sensitivity: strategySensitivity,
        },
      });
    },
  });

  const resetOptimizationMutation =
    optimizationMutation.reset;

  const applyStrategyPreset = (
    profile: Exclude<
      PricingDecisionStrategyProfile,
      'manual'
    >,
    sensitivity: number,
  ) => {
    setStrategyProfile(profile);
    setStrategySensitivity(sensitivity);
    resetOptimizationMutation();
  };

  const applyManualStrategySensitivity = (
    sensitivity: number,
  ) => {
    setStrategyProfile('manual');
    setStrategySensitivity(sensitivity);
    resetOptimizationMutation();
  };

  useEffect(() => {
    setWorkspaceMode(initialMode);
    setRecentTransactionsOpen(false);
    setQuantityInput('');

    setChartMetric(
      initialMode === 'optimization'
        ? 'margin_percentage'
        : 'revenue',
    );

    setStrategyProfile(
      DEFAULT_STRATEGY_PROFILE,
    );

    setStrategySensitivity(
      DEFAULT_STRATEGY_SENSITIVITY,
    );

    resetOptimizationMutation();
  }, [
    customerId,
    productId,
    initialMode,
    resetOptimizationMutation,
  ]);

  // An existing case opened from Overview goes straight to its own surface.
  // Do not force the mode on every render: the user can return to analysis.
  useEffect(() => {
    if (initialFollowUpId) {
      setWorkspaceMode('follow_up');
    }
  }, [initialFollowUpId]);

  const [
    historicalTimeGrain,
    setHistoricalTimeGrain,
  ] = useState<MarginCenterHistoricalTimeGrain>('auto');

  const [
    expandedTransactionId,
    setExpandedTransactionId,
  ] = useState<string | null>(null);

  const [recentPeriodFilter, setRecentPeriodFilter] =
    useState<RecentPeriodFilter>('all');

  useEffect(() => {
    setRecentPeriodFilter('all');
    setExpandedTransactionId(null);
  }, [
    customerId, productId, startDate, endDate, comparisonMode,
    comparisonStartDate, comparisonEndDate,
  ]);

  const queryParams =
    useMemo<MarginCenterCustomerProductContextParams>(
      () => ({
        start_date: startDate ?? undefined,
        end_date: endDate ?? undefined,
        comparison_mode: comparisonMode,
        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,
        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,
        time_grain: timeGrain,
        recent_transactions_limit: RECENT_OPERATIONS_LIMIT,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
        timeGrain,
      ],
    );

  const customerContextParams =
    useMemo<MarginCenterCustomerContextParams>(
      () => ({
        start_date:
          startDate ?? undefined,

        end_date:
          endDate ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,

        time_grain:
          timeGrain,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
        timeGrain,
      ],
    );

  const productContextParams =
    useMemo<MarginCenterProductContextParams>(
      () => ({
        start_date:
          startDate ?? undefined,

        end_date:
          endDate ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,

        time_grain:
          timeGrain,
      }),
      [
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        endDate,
        startDate,
        timeGrain,
      ],
    );

  const relationshipQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-product-context',
      customerId,
      productId,
      queryParams,
    ],
    queryFn: () =>
      getMarginCenterCustomerProductContext(
        customerId,
        productId ?? '',
        queryParams,
      ),
    enabled: Boolean(productId),
  });

  // DUX-03G: consulta contextual exhaustiva para decidir 0 / 1 / varios.
  // Dos filas bastan: page.has_more distingue "varios" sin cargar todo.
  const relationshipFollowUpInventoryQuery = useQuery({
    queryKey: [
      'margin-center',
      'follow-ups',
      'inventory',
      'relationship',
      customerId,
      productId,
      'open-direct',
      2,
    ],
    queryFn: () => getMarginCenterFollowUpInventory({
      context: 'relationship',
      customer_id: customerId,
      product_id: productId ?? undefined,
      status: 'open',
      scope: 'customer_product',
      match: 'direct',
      sort: 'updated_desc',
      limit: 2,
    }),
    enabled: Boolean(productId)
      && (workspaceMode === 'analysis' || workspaceMode === 'optimization')
      && (relationshipQuery.data?.status === 'ok'
        || relationshipQuery.data?.status === 'candidate_relationship'),
    staleTime: 20_000,
  });

  const customerContextQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'relationship-optimization-customer-context',
        customerId,
        customerContextParams,
      ],

      queryFn: () =>
        getMarginCenterCustomerContext(
          customerId,
          customerContextParams,
        ),

      enabled:
        Boolean(productId)
        && workspaceMode
          === 'optimization',

      staleTime:
        30_000,
    });

  const productContextQuery =
    useQuery({
      queryKey: [
        'margin-center',
        'relationship-optimization-product-context',
        productId,
        productContextParams,
      ],

      queryFn: () =>
        getMarginCenterProductContext(
          productId ?? '',
          productContextParams,
        ),

      enabled:
        Boolean(productId)
        && workspaceMode
          === 'optimization',

      staleTime:
        30_000,
    });

  const historyQuery = useQuery({
    queryKey: [
      'margin-center',
      'customer-product-history',
      customerId,
      productId,
      historicalTimeGrain,
    ],
    queryFn: () =>
      getMarginCenterCustomerProductHistory(
        customerId,
        productId ?? '',
        {
          time_grain: historicalTimeGrain,
        },
      ),
    enabled:
      Boolean(productId)
      && (
        timelineMode === 'history'
        || workspaceMode === 'optimization'
      ),
  });

  const context = relationshipQuery.data;
  const directOpenCases =
    relationshipFollowUpInventoryQuery.data?.items ?? [];
  const inventoryHasAdditionalOpenCases = Boolean(
    relationshipFollowUpInventoryQuery.data?.page.has_more,
  );
  const hasOneDirectOpenCase = (
    directOpenCases.length === 1
    && !inventoryHasAdditionalOpenCases
  );
  const hasSeveralDirectOpenCases = (
    !workspaceFollowUpId
    && (
      directOpenCases.length > 1
      || inventoryHasAdditionalOpenCases
    )
  );
  const selectedOpenCase = workspaceFollowUpId
    ? directOpenCases.find((item) => item.id === workspaceFollowUpId) ?? null
    : null;
  const visibleOpenCase = selectedOpenCase ?? (
    workspaceFollowUpId || !hasOneDirectOpenCase
      ? null
      : directOpenCases[0]
  );
  const directFollowUpId = workspaceFollowUpId ?? (
    hasOneDirectOpenCase ? directOpenCases[0].id : null
  );
  const commercialAccessDisabled = (
    !directFollowUpId
    && !hasSeveralDirectOpenCases
    && directOpenCases.length === 0
    && (
      context?.status !== 'ok'
      || relationshipFollowUpInventoryQuery.isPending
      || relationshipFollowUpInventoryQuery.isError
    )
  );
  const openRelationshipInventory = () => {
    const next = new URLSearchParams({
      context: 'relationship',
      customerId,
      productId: productId ?? '',
      returnTo: `${location.pathname}${location.search}`,
    });
    navigate(`/margin-center/control-comercial?${next.toString()}`);
  };
  const openRelationshipCommercial = () => {
    if (hasSeveralDirectOpenCases) {
      openRelationshipInventory();
      return;
    }
    if (directFollowUpId) {
      setWorkspaceFollowUpId(directFollowUpId);
      onFollowUpIdChange?.(directFollowUpId);
    }
    setWorkspaceMode('follow_up');
  };
  const recentTransactions = context?.recent_transactions ?? [];
  const recentGroups = recentTransactions.map((transaction) => ({
    transaction,
    group: context
      ? classifyRecentTransaction(transaction, context.selection)
      : 'history' as RecentPeriodGroup,
  }));
  const recentCounts: Record<RecentPeriodFilter, number> = {
    all: recentGroups.length,
    current: recentGroups.filter((item) => item.group === 'current').length,
    comparison: recentGroups.filter((item) => item.group === 'comparison').length,
    history: recentGroups.filter((item) => item.group === 'history').length,
  };
  const visibleRecentGroups = recentPeriodFilter === 'all'
    ? recentGroups
    : recentGroups.filter((item) => item.group === recentPeriodFilter);

  const historyContext = historyQuery.data;

  const optimizationResult =
    optimizationMutation.data ?? null;

  const optimizationReviewModeLabel =
    optimizationResult?.risks.review_mode
      === 'evaluation_possible'
      ? 'Evaluación posible'
      : optimizationResult?.risks.review_mode
        === 'review_required'
        ? 'Revisión requerida'
        : optimizationResult?.risks.review_mode
          === 'reference_only'
          ? 'Solo referencia'
          : '—';

  const analysisChartData =
    useMemo<RelationshipChartPoint[]>(() => {
      if (
        !context
        || (
          context.status !== 'ok'
          && context.status !== 'candidate_relationship'
        )
      ) {
        return [];
      }

      const byIndex =
        new Map<number, RelationshipChartPoint>();

      context.series.forEach((point) => {
        const existing =
          byIndex.get(point.bucket_index) ?? {
            bucketIndex: point.bucket_index,
            label:
              `Periodo ${point.bucket_index + 1}`,
            current: null,
            comparison: null,
            historical: null,
          };

        if (point.period === 'current') {
          existing.current =
            getSeriesValue(
              point,
              chartMetric,
            );

          existing.label =
            formatShortDate(
              point.covered_start,
            );
        } else {
          existing.comparison =
            getSeriesValue(
              point,
              chartMetric,
            );
        }

        byIndex.set(
          point.bucket_index,
          existing,
        );
      });

      return Array.from(
        byIndex.values(),
      ).sort(
        (left, right) =>
          left.bucketIndex - right.bucketIndex,
      );
    }, [chartMetric, context]);

  const historicalChartData =
    useMemo<RelationshipChartPoint[]>(() => {
      if (
        !historyContext
        || historyContext.status !== 'ok'
        || !historyContext.history
      ) {
        return [];
      }

      const effectiveTimeGrain =
        historyContext.history
          .effective_time_grain;

      return historyContext.series.map(
        (point) => ({
          bucketIndex:
            point.bucket_index,

          label:
            formatHistoricalBucketLabel(
              point.covered_start,
              effectiveTimeGrain,
            ),

          current: null,
          comparison: null,

          historical:
            getSeriesValue(
              point,
              chartMetric,
            ),
        }),
      );
    }, [
      chartMetric,
      historyContext,
    ]);

  const optimizationUsesActivityObservations =
    chartMetric === 'average_net_unit_price'
    || chartMetric === 'average_unit_cost'
    || chartMetric === 'margin_percentage';

  const optimizationHistorySeries =
    useMemo(
      () => {
        if (
          !historyContext
          || historyContext.status
            !== 'ok'
          || !historyContext.history
        ) {
          return [];
        }

        const fullSeries =
          historyContext.series;

        const effectiveTimeGrain =
          historyContext
            .history
            .effective_time_grain;

        if (
          optimizationUsesActivityObservations
        ) {
          const basePeriods =
            effectiveTimeGrain === 'day'
              ? 548
              : effectiveTimeGrain
                === 'week'
                ? 78
                : 18;

          const maximumPeriods =
            effectiveTimeGrain === 'day'
              ? 1095
              : effectiveTimeGrain
                === 'week'
                ? 156
                : 36;

          const minimumObservations = 8;
          const maximumObservations = 12;

          let startIndex =
            Math.max(
              0,
              fullSeries.length
              - basePeriods,
            );

          const getObservationIndexes = (
            fromIndex: number,
          ) =>
            fullSeries
              .map(
                (
                  point,
                  index,
                ) => ({
                  point,
                  index,
                }),
              )
              .filter(
                ({
                  point,
                  index,
                }) => {
                  if (
                    index < fromIndex
                  ) {
                    return false;
                  }

                  const value =
                    getSeriesValue(
                      point,
                      chartMetric,
                    );

                  return (
                    point.has_activity
                    && value !== null
                    && value !== undefined
                  );
                },
              )
              .map(
                ({
                  index,
                }) => index,
              );

          let observationIndexes =
            getObservationIndexes(
              startIndex,
            );

          if (
            observationIndexes.length
            < minimumObservations
          ) {
            startIndex =
              Math.max(
                0,
                fullSeries.length
                - maximumPeriods,
              );

            observationIndexes =
              getObservationIndexes(
                startIndex,
              );
          }

          if (
            observationIndexes.length
            > maximumObservations
          ) {
            startIndex =
              observationIndexes[
                observationIndexes.length
                - maximumObservations
              ];
          }

          return fullSeries.slice(
            startIndex,
          );
        }

        const visiblePeriods =
          effectiveTimeGrain === 'day'
            ? 90
            : effectiveTimeGrain
              === 'week'
              ? 26
              : 18;

        return fullSeries.slice(
          Math.max(
            0,
            fullSeries.length
            - visiblePeriods,
          ),
        );
      },
      [
        chartMetric,
        historyContext,
        optimizationUsesActivityObservations,
      ],
    );

  const optimizationHistoryActiveObservations =
    useMemo(
      () =>
        optimizationHistorySeries.filter(
          (point) => {
            const value =
              getSeriesValue(
                point,
                chartMetric,
              );

            return (
              point.has_activity
              && value !== null
              && value !== undefined
            );
          },
        ),
      [
        chartMetric,
        optimizationHistorySeries,
      ],
    );

  const optimizationHistoricalChartData =
    useMemo(
      () => {
        if (
          !historyContext
          || historyContext.status
            !== 'ok'
          || !historyContext.history
        ) {
          return [];
        }

        const effectiveTimeGrain =
          historyContext
            .history
            .effective_time_grain;

        return optimizationHistorySeries.map(
          (point) => ({
            bucketIndex:
              point.bucket_index,

            label:
              formatHistoricalBucketLabel(
                point.covered_start,
                effectiveTimeGrain,
              ),

            historical:
              getSeriesValue(
                point,
                chartMetric,
              ),

            units:
              point.units,
          }),
        );
      },
      [
        chartMetric,
        historyContext,
        optimizationHistorySeries,
      ],
    );

  const optimizationHistoricalTableRows =
    useMemo(
      () => (
        optimizationUsesActivityObservations
          ? [
            ...optimizationHistoryActiveObservations,
          ].reverse()
          : [
            ...optimizationHistorySeries,
          ].reverse()
      ),
      [
        optimizationHistoryActiveObservations,
        optimizationHistorySeries,
        optimizationUsesActivityObservations,
      ],
    );

  const optimizationHistoryWindowLabel =
    useMemo(
      () => {
        if (
          optimizationHistorySeries
            .length === 0
        ) {
          return null;
        }

        const firstPoint =
          optimizationHistorySeries[0];

        const lastPoint =
          optimizationHistorySeries[
            optimizationHistorySeries
              .length - 1
          ];

        return `${
          formatDate(
            firstPoint.covered_start,
          )
        } → ${
          formatDate(
            lastPoint.covered_end,
          )
        }`;
      },
      [
        optimizationHistorySeries,
      ],
    );

  const latestObservedHistoryPoint =
    useMemo(
      () => {
        if (
          !historyContext
          || historyContext.status
            !== 'ok'
        ) {
          return null;
        }

        for (
          let index =
            historyContext
              .series
              .length - 1;
          index >= 0;
          index -= 1
        ) {
          const point =
            historyContext
              .series[index];

          if (point.has_activity) {
            return point;
          }
        }

        return null;
      },
      [
        historyContext,
      ],
    );

  const optimizationDecisionComparison =
    useMemo(
      () => {
        if (
          !optimizationResult
          || !latestObservedHistoryPoint
        ) {
          return null;
        }

        if (
          chartMetric
          === 'margin_percentage'
        ) {
          const observed =
            latestObservedHistoryPoint
              .margin_percentage;

          const recommended =
            optimizationResult
              .recommendation
              .suggested_margin_pct;

          if (
            observed === null
            || recommended === null
          ) {
            return null;
          }

          return {
            observedLabel:
              'Último margen observado',

            observedValue:
              formatPct(
                observed,
              ),

            recommendationLabel:
              'Margen recomendado',

            recommendationValue:
              formatPct(
                recommended,
              ),

            deltaLabel:
              'Δ recomendado vs último',

            deltaValue:
              formatSignedPoints(
                recommended
                - observed,
              ),
          };
        }

        if (
          chartMetric
          === 'average_net_unit_price'
        ) {
          const observed =
            latestObservedHistoryPoint
              .average_net_unit_price;

          const recommended =
            optimizationResult
              .recommendation
              .suggested_price;

          if (
            observed === null
            || recommended === null
          ) {
            return null;
          }

          return {
            observedLabel:
              'Último precio observado',

            observedValue:
              formatCurrency(
                observed,
              ),

            recommendationLabel:
              'Precio recomendado',

            recommendationValue:
              formatCurrency(
                recommended,
              ),

            deltaLabel:
              'Δ recomendado vs último',

            deltaValue:
              formatSignedPct(
                calculateRelativeDeltaPct(
                  observed,
                  recommended,
                ),
              ),
          };
        }

        return null;
      },
      [
        chartMetric,
        latestObservedHistoryPoint,
        optimizationResult,
      ],
    );

  const chartData =
    timelineMode === 'history'
      ? historicalChartData
      : analysisChartData;

  const historySummary =
    historyContext?.status === 'ok'
      ? historyContext.history
      : null;

  const chartTitle =
    timelineMode === 'history'
      ? 'Histórico completo de la relación'
      : 'Evolución en el periodo analizado';

  const chartSubtitle =
    timelineMode === 'history'
      ? historySummary
        ? `${
            formatDate(
              historySummary.start_date,
            )
          } → ${
            formatDate(
              historySummary.end_date,
            )
          } · ${
            historyContext?.series.length ?? 0
          } periodos · granularidad ${
            getHistoricalTimeGrainLabel(
              historySummary.effective_time_grain,
            )
          }`
        : 'Toda la vida observada de la relación disponible en la cuenta.'
      : (
          'Detalle temporal del periodo seleccionado '
          + 'y su comparación equivalente.'
        );

  const open = Boolean(productId);

  const current = context?.current ?? null;
  const changes = context?.changes ?? null;
  const positioning = context?.positioning ?? null;
  const coverage = context?.coverage ?? null;

  const customerContext =
    customerContextQuery.data;

  const productContext =
    productContextQuery.data;

  const customerEvidence =
    customerContext?.status === 'ok'
      && customerContext.customer
      && customerContext.current
      ? {
          customer:
            customerContext.customer,

          current:
            customerContext.current,
        }
      : null;

  const productEvidence =
    productContext?.status === 'ok'
      && productContext.product
      && productContext.current
      ? {
          product:
            productContext.product,

          current:
            productContext.current,
        }
      : null;

  const contextEvidence =
    useMemo<
      PricingDecisionContextEvidence
    >(
      () => {
        const sections:
          PricingDecisionContextEvidence[
            'sections'
          ] = [];

        if (customerEvidence) {
          sections.push({
            kind: 'customer',
            eyebrow:
              'Referencia cliente',
            title:
              customerEvidence
                .customer
                .label,
            statusLabel:
              'Histórico disponible',
            description:
              'Comportamiento agregado del cliente '
              + 'en el periodo seleccionado.',
            metrics: [
              {
                label:
                  'Precio neto medio',
                value:
                  formatCurrency(
                    customerEvidence
                      .current
                      .average_net_unit_price,
                  ),
              },
              {
                label:
                  'Margen s/ingresos',
                value:
                  formatPct(
                    customerEvidence
                      .current
                      .margin_percentage,
                  ),
              },
              {
                label:
                  'Unidades',
                value:
                  formatNumber(
                    customerEvidence
                      .current
                      .units,
                  ),
              },
              {
                label:
                  'Productos activos',
                value:
                  formatNumber(
                    customerEvidence
                      .current
                      .active_products,
                  ),
              },
            ],
            attributes: [
              ...(
                customerEvidence
                  .customer
                  .attributes
                  .customer_segment
                  ? [{
                    label:
                      'Segmento',
                    value:
                      customerEvidence
                        .customer
                        .attributes
                        .customer_segment,
                  }]
                  : []
              ),
              ...(
                customerEvidence
                  .customer
                  .attributes
                  .region
                  ? [{
                    label:
                      'Región',
                    value:
                      customerEvidence
                        .customer
                        .attributes
                        .region,
                  }]
                  : []
              ),
              ...(
                customerEvidence
                  .customer
                  .attributes
                  .country
                  ? [{
                    label:
                      'País',
                    value:
                      customerEvidence
                        .customer
                        .attributes
                        .country,
                  }]
                  : []
              ),
            ],
          });
        }

        if (productEvidence) {
          sections.push({
            kind: 'product',
            eyebrow:
              'Referencia producto',
            title:
              productEvidence
                .product
                .label,
            statusLabel:
              'Histórico disponible',
            description:
              'Comportamiento agregado del producto '
              + 'en el periodo seleccionado.',
            metrics: [
              {
                label:
                  'Precio neto medio',
                value:
                  formatCurrency(
                    productEvidence
                      .current
                      .average_net_unit_price,
                  ),
              },
              {
                label:
                  'Margen s/ingresos',
                value:
                  formatPct(
                    productEvidence
                      .current
                      .margin_percentage,
                  ),
              },
              {
                label:
                  'Unidades',
                value:
                  formatNumber(
                    productEvidence
                      .current
                      .units,
                  ),
              },
              {
                label:
                  'Clientes activos',
                value:
                  formatNumber(
                    productEvidence
                      .current
                      .active_customers,
                  ),
              },
            ],
            attributes: [
              ...(
                productEvidence
                  .product
                  .attributes
                  .product_category
                  ? [{
                    label:
                      'Categoría',
                    value:
                      productEvidence
                        .product
                        .attributes
                        .product_category,
                  }]
                  : []
              ),
              ...(
                productEvidence
                  .product
                  .attributes
                  .product_brand
                  ? [{
                    label:
                      'Marca',
                    value:
                      productEvidence
                        .product
                        .attributes
                        .product_brand,
                  }]
                  : []
              ),
              ...(
                productEvidence
                  .product
                  .attributes
                  .standard_cost
                  !== null
                && productEvidence
                  .product
                  .attributes
                  .standard_cost
                  !== undefined
                  ? [{
                    label:
                      'Coste estándar',
                    value:
                      formatCurrency(
                        productEvidence
                          .product
                          .attributes
                          .standard_cost,
                      ),
                  }]
                  : []
              ),
            ],
          });
        }

        return {
          description:
            'La relación concreta se resume arriba en '
            + '“Así veníamos trabajando”. Estas referencias '
            + 'añaden el comportamiento global del cliente '
            + 'y del producto para interpretar la recomendación.',
          sections,
        };
      },
      [
        customerEvidence,
        productEvidence,
      ],
    );

  const currentPositioning =
    positioning?.current ?? null;

  const productAttributes = context?.product?.attributes;

  const visibleProductAttributes = [
    {
      label: 'Categoría',
      value: productAttributes?.product_category,
    },
    {
      label: 'Marca',
      value: productAttributes?.product_brand,
    },
    {
      label: 'Unidad registrada',
      value: productAttributes?.unit_of_measure,
    },
  ].filter(
    (attribute) => Boolean(attribute.value),
  );

  const coverageNeedsAttention =
    context?.status === 'ok'
    && coverage !== null
    && coverage.current.cost_coverage_pct < 100;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: {
            xs: '100vw',
            md: '94vw',
          },
          maxWidth: 1500,
          height: {
            xs: '100dvh',
            md: '90vh',
          },
          maxHeight: {
            xs: '100dvh',
            md: 980,
          },
          m: {
            xs: 0,
            md: 2,
          },
          borderRadius: {
            xs: 0,
            md: 3.5,
          },
          bgcolor: '#0b1220',
          backgroundImage: 'none',
          border:
            '1px solid rgba(148,163,184,0.18)',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: {
            xs: 2,
            md: 2.25,
          },
          py: {
            xs: 1.25,
            lg: 1.05,
          },
          borderBottom:
            '1px solid rgba(148,163,184,0.14)',
          background:
            'linear-gradient(100deg, rgba(14,165,233,0.08) 0%, rgba(15,23,42,0.96) 34%, rgba(99,102,241,0.05) 100%)',
          boxShadow:
            '0 10px 30px rgba(2,6,23,0.20)',
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          spacing={{
            xs: 1.25,
            lg: 2,
          }}
          alignItems={{
            xs: 'stretch',
            lg: 'center',
          }}
          justifyContent="space-between"
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              <Typography
                variant="overline"
                sx={{
                  color: '#38bdf8',
                  fontWeight: 850,
                  letterSpacing: 0.9,
                  lineHeight: 1,
                }}
              >
                Relación cliente-producto
              </Typography>

              <Chip
                size="small"
                label={
                  workspaceMode === 'analysis'
                    ? 'Análisis'
                    : workspaceMode === 'follow_up'
                      ? 'Seguimiento'
                      : 'Optimización'
                }
                sx={{
                  height: 22,
                  color:
                    workspaceMode === 'optimization'
                      ? '#e0f2fe'
                      : '#cbd5e1',
                  border:
                    '1px solid rgba(56,189,248,0.20)',
                  bgcolor:
                    workspaceMode === 'optimization'
                      ? 'rgba(14,165,233,0.12)'
                      : 'rgba(15,23,42,0.42)',
                  '& .MuiChip-label': {
                    px: 0.9,
                    fontSize: 11,
                    fontWeight: 800,
                  },
                }}
              />

              {visibleProductAttributes.map((attribute) => (
                <Chip
                  key={attribute.label}
                  size="small"
                  variant="outlined"
                  label={
                    `${attribute.label}: ${
                      attribute.value ?? '—'
                    }`
                  }
                  sx={{
                    height: 22,
                    color: '#94a3b8',
                    borderColor:
                      'rgba(148,163,184,0.24)',
                    bgcolor:
                      'rgba(15,23,42,0.36)',
                    '& .MuiChip-label': {
                      px: 0.8,
                      fontSize: 11,
                    },
                  }}
                />
              ))}
            </Stack>

            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={{
                xs: 0.15,
                sm: 1.25,
              }}
              alignItems={{
                xs: 'flex-start',
                sm: 'baseline',
              }}
              sx={{ mt: 0.45 }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                  lineHeight: 1.15,
                }}
              >
                {context?.customer?.label ?? customerId}
                {' × '}
                {context?.product?.label ?? productId ?? 'Producto'}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                Cliente {customerId}
                {' · '}
                Producto {productId ?? '—'}
              </Typography>
            </Stack>
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            <Button
              size="small"
              variant={
                workspaceMode === 'analysis'
                  ? 'contained'
                  : 'outlined'
              }
              disabled={
                workspaceMode !== 'follow_up'
                && context?.status !== 'ok'
                && context?.status !== 'candidate_relationship'
              }
              onClick={() => {
                if (
                  workspaceMode
                  === 'analysis'
                ) {
                  setChartMetric(
                    'margin_percentage',
                  );

                  setWorkspaceMode(
                    'optimization',
                  );

                  return;
                }

                setWorkspaceMode(
                  'analysis',
                );
              }}
              sx={{
                flexShrink: 0,
                minWidth: 132,
                textTransform: 'none',
                fontWeight: 850,
                boxShadow: 'none',
              }}
            >
              {workspaceMode === 'analysis'
                ? 'Optimizar precio'
                : 'Volver al análisis'}
            </Button>

            {workspaceMode === 'optimization' && productId && (
              <Button
                size="small"
                variant="outlined"
                disabled={commercialAccessDisabled}
                onClick={openRelationshipCommercial}
                sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 800 }}
              >
                {directFollowUpId
                  ? 'Ver seguimiento'
                  : hasSeveralDirectOpenCases
                    ? 'Elegir expediente'
                    : 'Control comercial'}
              </Button>
            )}

            {context && (
              <MarginCenterTemporalContextBar
                selection={context.selection}
                dataset={context.dataset}
                compact
              />
            )}

            <IconButton
              aria-label="Cerrar contexto de relación"
              onClick={onClose}
              sx={{
                flexShrink: 0,
                color: '#94a3b8',
                border:
                  '1px solid rgba(148,163,184,0.12)',
                bgcolor:
                  'rgba(15,23,42,0.30)',
                '&:hover': {
                  color: '#f8fafc',
                  bgcolor:
                    'rgba(148,163,184,0.10)',
                },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {relationshipQuery.isFetching && (
        <LinearProgress />
      )}

      <DialogContent
        sx={{
          p: {
            xs: 2,
            md: 2.25,
          },
          overflowY: 'auto',
          bgcolor: '#08111f',
          backgroundImage: [
            'radial-gradient(circle at 8% 0%, rgba(14,165,233,0.055), transparent 30%)',
            'radial-gradient(circle at 92% 18%, rgba(99,102,241,0.035), transparent 25%)',
          ].join(','),
        }}
      >
        {relationshipQuery.isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() =>
                  relationshipQuery.refetch()
                }
              >
                Reintentar
              </Button>
            }
          >
            No se pudo cargar el contexto de la relación.
          </Alert>
        )}

        {context?.status === 'empty_dataset' && (
          <Alert severity="info">
            Esta cuenta todavía no dispone de actividad
            transaccional.
          </Alert>
        )}

        {context?.status === 'customer_not_found' && (
          <Alert severity="warning">
            No se ha encontrado el cliente en la cuenta actual.
          </Alert>
        )}

        {context?.status === 'product_not_found' && (
          <Alert severity="warning">
            No se ha encontrado el producto en la cuenta actual.
          </Alert>
        )}

        {context?.status === 'candidate_relationship' && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
          >
            Esta combinación cliente-producto no tiene histórico
            observado hasta el final del periodo seleccionado.
            Se muestra como relación candidata.
          </Alert>
        )}

        {workspaceMode === 'analysis' && productId && (
          context?.status === 'ok'
          || context?.status === 'candidate_relationship'
        ) && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              px: { xs: 1.75, md: 2 },
              py: 1.5,
              borderRadius: 2.5,
              border: '1px solid rgba(56,189,248,0.22)',
              bgcolor: 'rgba(14,165,233,0.055)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="overline"
                  sx={{ color: '#38bdf8', fontWeight: 800, letterSpacing: 0.7 }}
                >
                  Control comercial
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography
                    variant="subtitle1"
                    sx={{ color: '#f8fafc', fontWeight: 800, lineHeight: 1.25 }}
                  >
                    {visibleOpenCase
                      ? 'Seguimiento comercial abierto'
                      : directFollowUpId
                        ? 'Expediente seleccionado'
                        : hasSeveralDirectOpenCases
                          ? 'Varias investigaciones abiertas'
                          : 'Investigación de esta relación'}
                  </Typography>
                  {visibleOpenCase && (
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      label={visibleOpenCase.status === 'in_review' ? 'En revisión' : 'Nuevo'}
                    />
                  )}
                </Stack>
                {visibleOpenCase ? (
                  <Stack spacing={0.35} sx={{ mt: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                      {visibleOpenCase.next_action || 'Siguiente acción no definida.'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                      Actualizado: {new Date(visibleOpenCase.updated_at).toLocaleDateString('es-ES')}
                      {visibleOpenCase.next_review_at
                        ? ` · Próxima revisión: ${new Date(visibleOpenCase.next_review_at).toLocaleDateString('es-ES')}`
                        : ''}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.45 }}>
                    {directFollowUpId
                      ? 'Continúa el expediente seleccionado sin volver a buscarlo.'
                      : hasSeveralDirectOpenCases
                        ? 'Hay varios seguimientos abiertos de esta relación. Elige cuál consultar.'
                        : relationshipFollowUpInventoryQuery.isLoading
                          ? 'Consultando los seguimientos de esta relación…'
                          : relationshipFollowUpInventoryQuery.isError
                            ? 'No se pudo consultar el estado del seguimiento. No se iniciará una investigación sin verificarlo.'
                            : 'Consulta o inicia una investigación sobre esta combinación cliente-producto.'}
                  </Typography>
                )}
                {context?.status === 'candidate_relationship' && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: '#fbbf24', mt: 0.5 }}
                  >
                    La creación de Seguimientos v1 exige una relación históricamente observada.
                  </Typography>
                )}
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={commercialAccessDisabled}
                onClick={openRelationshipCommercial}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' }, textTransform: 'none', fontWeight: 800 }}
              >
                {directFollowUpId
                  ? 'Ver seguimiento'
                  : hasSeveralDirectOpenCases
                    ? 'Elegir expediente'
                    : 'Abrir control comercial'}
              </Button>
              {!hasSeveralDirectOpenCases && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                  onClick={openRelationshipInventory}
                >
                  Ver todos los expedientes
                </Button>
              )}
            </Stack>
          </Paper>
        )}

        {workspaceMode === 'follow_up' && productId && (
          <Box sx={{ width: '100%' }}>
            <CustomerRelationshipFollowUpPanel
              customerId={customerId}
              productId={productId}
              initialFollowUpId={workspaceFollowUpId}
              cadenceAttentionOccurrenceDate={
                cadenceAttentionOccurrenceDate
              }
              onFollowUpIdChange={(id) => {
                setWorkspaceFollowUpId(id);
                onFollowUpIdChange?.(id);
              }}
              onOpenInventory={openRelationshipInventory}
            />
          </Box>
        )}

        {workspaceMode === 'optimization'
          && (
            context?.status === 'ok'
            || context?.status === 'candidate_relationship'
          )
          && current
          && coverage
          && (
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border:
                    '1px solid rgba(56,189,248,0.20)',
                  background:
                    'linear-gradient(135deg, rgba(14,165,233,0.085) 0%, rgba(15,23,42,0.66) 42%, rgba(99,102,241,0.045) 100%)',
                  boxShadow:
                    '0 16px 42px rgba(2,6,23,0.22)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: {
                      xs: 1.75,
                      md: 2,
                    },
                    py: 1.6,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: '#38bdf8',
                      fontWeight: 850,
                      letterSpacing: 0.8,
                    }}
                  >
                    Contexto de decisión
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      mt: 0.15,
                      color: '#f8fafc',
                      fontWeight: 900,
                    }}
                  >
                    Optimización de precio
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      maxWidth: 760,
                      color: '#94a3b8',
                      lineHeight: 1.6,
                    }}
                  >
                    {context.status === 'candidate_relationship'
                      ? (
                        'Cliente y producto están fijados, pero no '
                        + 'existe histórico transaccional conjunto. '
                        + 'La evaluación será prospectiva y utilizará '
                        + 'únicamente la evidencia disponible.'
                      )
                      : (
                        'La relación está fijada y preparada para una '
                        + 'evaluación explícita. Entrar en este modo no '
                        + 'ejecuta ningún cálculo ni modifica ningún precio.'
                      )}
                  </Typography>
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(5, minmax(0, 1fr))',
                    },
                  }}
                >
                  {context.status === 'candidate_relationship' ? (
                    <>
                      <CompactMetric
                        label="Contexto"
                        value="Prospectivo"
                      />

                      <CompactMetric
                        label="Cliente"
                        value="Resuelto"
                      />

                      <CompactMetric
                        label="Producto"
                        value="Resuelto"
                      />

                      <CompactMetric
                        label="Histórico conjunto"
                        value="No disponible"
                      />

                      <CompactMetric
                        label="Coste"
                        value="Se resolverá al ejecutar"
                      />
                    </>
                  ) : (
                    <>
                      <CompactMetric
                        label="Precio neto medio observado"
                        value={formatCurrency(
                          current.average_net_unit_price,
                        )}
                      />

                      <CompactMetric
                        label="Coste unitario medio observado"
                        value={formatCurrency(
                          current.average_unit_cost,
                        )}
                      />

                      <CompactMetric
                        label="Margen sobre ingresos observado"
                        value={formatPct(
                          current.margin_percentage,
                        )}
                      />

                      <CompactMetric
                        label="Cantidad observada"
                        value={formatNumber(
                          current.units,
                        )}
                      />

                      <CompactMetric
                        label="Ingresos observados"
                        value={formatCurrency(
                          current.revenue,
                        )}
                      />
                    </>
                  )}
                </Box>

                <Divider />

                <Box
                  sx={{
                    px: {
                      xs: 1.75,
                      md: 2,
                    },
                    py: 1.5,
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row',
                    }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{
                      xs: 'flex-start',
                      md: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{
                          color: '#94a3b8',
                          fontWeight: 800,
                          letterSpacing: 0.8,
                        }}
                      >
                        Configuración de la decisión
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.15,
                          color: '#cbd5e1',
                          fontWeight: 750,
                        }}
                      >
                        Define cuánto peso tendrá la señal del
                        optimizador frente a la inercia comercial.
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={`${
                        strategyProfile === 'conservador'
                          ? 'Conservadora'
                          : strategyProfile === 'equilibrado'
                            ? 'Equilibrada'
                            : strategyProfile === 'agresivo'
                              ? 'Agresiva'
                              : 'Manual'
                      } · ${strategySensitivity}%`}
                      variant="outlined"
                      sx={{
                        color: '#bae6fd',
                        borderColor:
                          'rgba(56,189,248,0.28)',
                        bgcolor:
                          'rgba(14,165,233,0.06)',
                        fontWeight: 800,
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      mt: 1.4,
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        lg:
                          'minmax(0, 1.15fr) minmax(360px, 0.85fr)',
                      },
                      gap: {
                        xs: 1.75,
                        lg: 3,
                      },
                      alignItems: 'start',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mb: 0.7,
                          color: '#64748b',
                          fontWeight: 750,
                        }}
                      >
                        Perfil estratégico
                      </Typography>

                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={
                          strategyProfile === 'manual'
                            ? null
                            : strategyProfile
                        }
                        onChange={(
                          _,
                          nextProfile:
                            Exclude<
                              PricingDecisionStrategyProfile,
                              'manual'
                            >
                            | null,
                        ) => {
                          if (!nextProfile) return;

                          const preset =
                            STRATEGY_PRESETS.find(
                              (item) =>
                                item.value
                                === nextProfile,
                            );

                          if (!preset) return;

                          applyStrategyPreset(
                            preset.value,
                            preset.sensitivity,
                          );
                        }}
                        disabled={
                          optimizationMutation.isPending
                        }
                        sx={{
                          width: '100%',
                          '& .MuiToggleButton-root': {
                            flex: 1,
                            minWidth: 0,
                            px: 1,
                            py: 0.9,
                            textTransform: 'none',
                            color: '#94a3b8',
                            borderColor:
                              'rgba(148,163,184,0.18)',
                            '&.Mui-selected': {
                              color: '#e0f2fe',
                              bgcolor:
                                'rgba(56,189,248,0.12)',
                            },
                            '&.Mui-selected:hover': {
                              bgcolor:
                                'rgba(56,189,248,0.16)',
                            },
                          },
                        }}
                      >
                        {STRATEGY_PRESETS.map(
                          (preset) => (
                            <ToggleButton
                              key={preset.value}
                              value={preset.value}
                            >
                              <Box
                                sx={{
                                  minWidth: 0,
                                  textAlign: 'left',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'inherit',
                                    fontWeight: 850,
                                  }}
                                >
                                  {preset.label}
                                  {' · '}
                                  {preset.sensitivity}%
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: {
                                      xs: 'none',
                                      md: 'block',
                                    },
                                    mt: 0.15,
                                    color: '#64748b',
                                    lineHeight: 1.35,
                                    whiteSpace: 'normal',
                                  }}
                                >
                                  {preset.description}
                                </Typography>
                              </Box>
                            </ToggleButton>
                          ),
                        )}
                      </ToggleButtonGroup>
                    </Box>

                    <Box>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        spacing={2}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                            fontWeight: 750,
                          }}
                        >
                          Intensidad estratégica
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#38bdf8',
                            fontWeight: 900,
                          }}
                        >
                          {strategySensitivity} %
                        </Typography>
                      </Stack>

                      <Slider
                        value={strategySensitivity}
                        min={0}
                        max={100}
                        step={1}
                        disabled={
                          optimizationMutation.isPending
                        }
                        onChange={(_, value) => {
                          const nextValue =
                            Array.isArray(value)
                              ? value[0]
                              : value;

                          applyManualStrategySensitivity(
                            nextValue,
                          );
                        }}
                        marks={[
                          {
                            value: 0,
                            label: 'Inercia',
                          },
                          {
                            value: 50,
                            label: 'Equilibrio',
                          },
                          {
                            value: 100,
                            label: 'IA',
                          },
                        ]}
                        sx={{
                          mt: 0.5,
                          color: '#38bdf8',
                          '& .MuiSlider-markLabel': {
                            color: '#64748b',
                            fontSize: 11,
                          },
                        }}
                      />

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.6,
                          color: '#64748b',
                          lineHeight: 1.5,
                        }}
                      >
                        0 % prioriza la base inercial y
                        100 % da el máximo peso permitido a
                        la señal IA. Este porcentaje no es el
                        riesgo estimado de la recomendación.
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                <Box
                  sx={{
                    px: {
                      xs: 1.75,
                      md: 2,
                    },
                    py: 1.4,
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    spacing={1.25}
                    justifyContent="space-between" 
                    alignItems={{
                      xs: 'flex-start',
                      sm: 'center',
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 800,
                        }}
                      >
                        Relación fijada
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.2,
                          color: '#64748b',
                        }}
                      >
                        Cliente {customerId}
                        {' · '}
                        Producto {productId ?? '—'}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Cantidad"
                        value={quantityInput}
                        disabled={
                          optimizationMutation.isPending
                        }
                        onChange={(event) => {
                          setQuantityInput(
                            event.target.value,
                          );
                          resetOptimizationMutation();
                        }}
                        sx={{
                          width: 150,
                        }}
                      />

                      <Chip
                        size="small"
                        label={
                          optimizationMutation.isPending
                            ? 'Ejecutando'
                            : optimizationResult
                              ? 'Resultado disponible'
                              : 'Pendiente de ejecución'
                        }
                        variant="outlined"
                        sx={{
                          color: '#94a3b8',
                          borderColor:
                            'rgba(148,163,184,0.24)',
                        }}
                      />

                      <Button
                        size="small"
                        variant="contained"
                        disabled={
                          optimizationMutation.isPending
                          || !productId
                        }
                        onClick={() =>
                          optimizationMutation.mutate()
                        }
                        sx={{
                          textTransform: 'none',
                          fontWeight: 850,
                          boxShadow: 'none',
                        }}
                      >
                        {optimizationMutation.isPending
                          ? 'Ejecutando...'
                          : optimizationResult
                            ? 'Ejecutar de nuevo'
                            : 'Ejecutar optimización'}
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              {optimizationMutation.isPending && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(56,189,248,0.16)',
                    bgcolor:
                      'rgba(15,23,42,0.58)',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#cbd5e1',
                      fontWeight: 750,
                    }}
                  >
                    Ejecutando optimización
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.3,
                      mb: 1.2,
                      color: '#64748b',
                    }}
                  >
                    El cálculo utiliza el motor de optimización
                    existente para esta relación concreta.
                  </Typography>

                  <LinearProgress />
                </Paper>
              )}

              {optimizationMutation.isError && (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() =>
                        optimizationMutation.mutate()
                      }
                    >
                      Reintentar
                    </Button>
                  }
                >
                  No se pudo ejecutar la optimización de esta
                  relación. No se ha modificado ningún precio.
                </Alert>
              )}

              {optimizationResult?.status
                === 'no_recommendation' && (
                <Alert severity="info">
                  El optimizador terminó la evaluación, pero no
                  produjo una recomendación aplicable para esta
                  relación.
                </Alert>
              )}

              {optimizationResult?.status === 'ok' && (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(129,140,248,0.18)',
                    background:
                      'linear-gradient(135deg, rgba(99,102,241,0.075) 0%, rgba(15,23,42,0.72) 42%, rgba(14,165,233,0.045) 100%)',
                    boxShadow:
                      '0 16px 42px rgba(2,6,23,0.20)',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      px: {
                        xs: 1.75,
                        md: 2,
                      },
                      py: 1.5,
                    }}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'flex-start',
                        sm: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{
                            color: '#a5b4fc',
                            fontWeight: 850,
                            letterSpacing: 0.8,
                          }}
                        >
                          Resultado del optimizador
                        </Typography>

                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 900,
                          }}
                        >
                          Recomendación para esta relación
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.75}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Chip
                          size="small"
                          label={
                            `${getStrategyProfileLabel(
                              optimizationResult
                                .request
                                .strategy_profile,
                            )} · ${
                              numberFormatter.format(
                                optimizationResult
                                  .request
                                  .strategy_sensitivity,
                              )
                            } %`
                          }
                          variant="outlined"
                          sx={{
                            color: '#bae6fd',
                            borderColor:
                              'rgba(56,189,248,0.28)',
                          }}
                        />

                        <Chip
                          size="small"
                          label={optimizationReviewModeLabel}
                          variant="outlined"
                          sx={{
                            color: '#cbd5e1',
                            borderColor:
                              'rgba(165,180,252,0.30)',
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      p: {
                        xs: 1.25,
                        md: 1.5,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          lg:
                            'minmax(0, 0.9fr) minmax(0, 1.35fr) minmax(0, 0.9fr)',
                        },
                        gap: 1,
                        alignItems: 'stretch',
                      }}
                    >
                      <DecisionScenarioCard
                        label="Base / Inercial"
                        price={
                          optimizationResult
                            .scenarios
                            .inertial_price
                        }
                        marginPct={
                          optimizationResult
                            .scenarios
                            .inertial_margin_pct
                        }
                        markupPct={
                          optimizationResult
                            .scenarios
                            .inertial_markup_pct
                        }
                        cost={
                          optimizationResult
                            .recommendation
                            .cost_used
                        }
                        costLabel="Coste optimizador"
                        deltaVsObservedPct={
                          calculateRelativeDeltaPct(
                            current
                              .average_net_unit_price,
                            optimizationResult
                              .scenarios
                              .inertial_price,
                          )
                        }
                        details={[]}
                      />

                      <RecommendationDecisionCard
                        price={
                          optimizationResult
                            .recommendation
                            .suggested_price
                        }
                        marginPct={
                          optimizationResult
                            .recommendation
                            .suggested_margin_pct
                        }
                        markupPct={
                          optimizationResult
                            .recommendation
                            .suggested_markup_pct
                        }
                        cost={
                          optimizationResult
                            .recommendation
                            .cost_used
                        }
                        deltaVsObservedPct={
                          calculateRelativeDeltaPct(
                            current
                              .average_net_unit_price,
                            optimizationResult
                              .recommendation
                              .suggested_price,
                          )
                        }
                        haircutVsMaxAiPct={
                          optimizationResult
                            .scenarios
                            .haircut_vs_max_ai_pct
                        }
                      />

                      <DecisionScenarioCard
                        label="Máximo IA"
                        price={
                          optimizationResult
                            .scenarios
                            .max_ai_price
                        }
                        marginPct={
                          optimizationResult
                            .scenarios
                            .max_ai_margin_pct
                        }
                        markupPct={
                          optimizationResult
                            .scenarios
                            .max_ai_markup_pct
                        }
                        cost={
                          optimizationResult
                            .recommendation
                            .cost_used
                        }
                        costLabel="Coste optimizador"
                        deltaVsObservedPct={
                          calculateRelativeDeltaPct(
                            current
                              .average_net_unit_price,
                            optimizationResult
                              .scenarios
                              .max_ai_price,
                          )
                        }
                        details={
                          optimizationResult
                            .request
                            .quantity === null
                            ? []
                            : [
                                {
                                  label:
                                    'Volumen simulado',
                                  value: formatNumber(
                                    optimizationResult
                                      .scenarios
                                      .ai_simulated_volume,
                                  ),
                                },
                              ]
                        }
                      />
                    </Box>

                    {context.status === 'ok' && (
                      <>
                        <ObservedDecisionStrip
                          price={
                            current.average_net_unit_price
                          }
                          marginPct={
                            current.margin_percentage
                          }
                          cost={
                            current.average_unit_cost
                          }
                          units={current.units}
                          revenue={current.revenue}
                          coveragePct={
                            coverage.current
                              .cost_coverage_pct
                          }
                          recentTransactionsCount={
                            context
                              .recent_transactions
                              .length
                          }
                          recentTransactionsOpen={
                            recentTransactionsOpen
                          }
                          onToggleRecentTransactions={() =>
                            setRecentTransactionsOpen(
                              (currentValue) =>
                                !currentValue,
                            )
                          }
                        />

                        <Collapse
                          in={recentTransactionsOpen}
                          unmountOnExit
                        >
                          <RecentTransactionsDecisionPanel
                            transactions={
                              context
                                .recent_transactions
                            }
                          />
                        </Collapse>
                      </>
                    )}

                    {contextEvidence
                      .sections
                      .length > 0 && (
                      <Box
                        sx={{
                          mt: 1.25,
                        }}
                      >
                        <PricingDecisionContextEvidencePanel
                          evidence={
                            contextEvidence
                          }
                        />
                      </Box>
                    )}

                    {context.status === 'ok' && (
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1.25,
                          borderRadius: 2.5,

                          border:
                            '1px solid rgba(56,189,248,0.16)',

                          bgcolor:
                            'rgba(8,17,31,0.64)',

                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            px: {
                              xs: 1.5,
                              md: 1.75,
                            },

                            py: 1.35,
                          }}
                        >
                          <Stack
                            direction={{
                              xs: 'column',
                              lg: 'row',
                            }}
                            spacing={1.25}
                            justifyContent="space-between"
                            alignItems={{
                              xs: 'stretch',
                              lg: 'flex-start',
                            }}
                          >
                            <Box
                              sx={{
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="overline"
                                sx={{
                                  color: '#7dd3fc',
                                  fontWeight: 850,
                                  letterSpacing: 0.75,
                                }}
                              >
                                Evidencia histórica de la relación
                              </Typography>

                              <Typography
                                variant="subtitle1"
                                sx={{
                                  mt: 0.15,
                                  color: '#f8fafc',
                                  fontWeight: 850,
                                }}
                              >
                                Precio, volumen y margen a lo largo del tiempo
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.3,
                                  color: '#64748b',
                                  lineHeight: 1.5,
                                  maxWidth: 820,
                                }}
                              >
                                Utiliza el histórico observado para
                                interpretar la recomendación. Para precio,
                                coste y margen se muestran las últimas
                                observaciones con actividad; ingresos y
                                cantidad mantienen una ventana temporal
                                continua. No se infiere causalidad ni
                                elasticidad a partir de esta visualización.
                              </Typography>
                            </Box>

                            <Stack
                              direction={{
                                xs: 'column',
                                sm: 'row',
                              }}
                              spacing={0.75}
                              useFlexGap
                              flexWrap="wrap"
                            >
                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={
                                  historicalTimeGrain
                                }
                                onChange={(
                                  _,
                                  nextTimeGrain:
                                    MarginCenterHistoricalTimeGrain
                                    | null,
                                ) => {
                                  if (
                                    nextTimeGrain
                                  ) {
                                    setHistoricalTimeGrain(
                                      nextTimeGrain,
                                    );
                                  }
                                }}
                                sx={{
                                  flexWrap: 'wrap',

                                  '& .MuiToggleButton-root': {
                                    px: 1,
                                    py: 0.35,
                                    textTransform: 'none',
                                    color: '#64748b',

                                    borderColor:
                                      'rgba(148,163,184,0.14)',

                                    '&.Mui-selected': {
                                      color: '#cbd5e1',

                                      bgcolor:
                                        'rgba(148,163,184,0.08)',
                                    },
                                  },
                                }}
                              >
                                <ToggleButton
                                  value="auto"
                                >
                                  Auto
                                </ToggleButton>

                                <ToggleButton
                                  value="day"
                                >
                                  Día
                                </ToggleButton>

                                <ToggleButton
                                  value="week"
                                >
                                  Semana
                                </ToggleButton>

                                <ToggleButton
                                  value="month"
                                >
                                  Mes
                                </ToggleButton>
                              </ToggleButtonGroup>

                              <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={
                                  chartMetric
                                }
                                onChange={(
                                  _,
                                  nextMetric:
                                    ChartMetric
                                    | null,
                                ) => {
                                  if (
                                    nextMetric
                                  ) {
                                    setChartMetric(
                                      nextMetric,
                                    );
                                  }
                                }}
                                sx={{
                                  flexWrap: 'wrap',

                                  '& .MuiToggleButton-root': {
                                    px: 1,
                                    py: 0.35,
                                    textTransform: 'none',
                                    color: '#94a3b8',

                                    borderColor:
                                      'rgba(148,163,184,0.18)',

                                    '&.Mui-selected': {
                                      color: '#f8fafc',

                                      bgcolor:
                                        'rgba(56,189,248,0.12)',
                                    },
                                  },
                                }}
                              >
                                {CHART_METRICS.map(
                                  (
                                    metric,
                                  ) => (
                                    <ToggleButton
                                      key={
                                        metric.value
                                      }
                                      value={
                                        metric.value
                                      }
                                    >
                                      {metric.label}
                                    </ToggleButton>
                                  ),
                                )}
                              </ToggleButtonGroup>
                            </Stack>
                          </Stack>

                          {historySummary
                            && optimizationHistoryWindowLabel
                            && (
                            <Stack
                              direction="row"
                              spacing={1}
                              useFlexGap
                              flexWrap="wrap"
                              sx={{
                                mt: 1,
                              }}
                            >
                              <Chip
                                size="small"
                                label={
                                  `Ventana de decisión · ${
                                    optimizationHistoryWindowLabel
                                  }`
                                }
                                variant="outlined"
                                sx={{
                                  color: '#94a3b8',

                                  borderColor:
                                    'rgba(148,163,184,0.16)',
                                }}
                              />

                              <Chip
                                size="small"
                                label={
                                  optimizationUsesActivityObservations
                                    ? `${
                                      optimizationHistoryActiveObservations
                                        .length
                                    } observaciones con actividad`
                                    : `${
                                      optimizationHistorySeries
                                        .length
                                    } periodos recientes`
                                }
                                variant="outlined"
                                sx={{
                                  color: '#94a3b8',

                                  borderColor:
                                    'rgba(148,163,184,0.16)',
                                }}
                              />

                              <Chip
                                size="small"
                                label={
                                  `Granularidad ${
                                    getHistoricalTimeGrainLabel(
                                      historySummary
                                        .effective_time_grain,
                                    )
                                  }`
                                }
                                variant="outlined"
                                sx={{
                                  color: '#94a3b8',

                                  borderColor:
                                    'rgba(148,163,184,0.16)',
                                }}
                              />
                            </Stack>
                          )}
                        </Box>

                        <Divider />

                        {optimizationDecisionComparison
                          && (
                            <Box
                              sx={{
                                px: {
                                  xs: 1.5,
                                  md: 1.75,
                                },

                                py: 1.1,

                                bgcolor:
                                  'rgba(15,23,42,0.24)',
                              }}
                            >
                              <Stack
                                direction={{
                                  xs: 'column',
                                  sm: 'row',
                                }}
                                spacing={{
                                  xs: 0.8,
                                  sm: 2.5,
                                }}
                                useFlexGap
                                flexWrap="wrap"
                              >
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: '#64748b',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .observedLabel
                                    }
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#e2e8f0',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .observedValue
                                    }
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: '#64748b',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .recommendationLabel
                                    }
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#fbbf24',
                                      fontWeight: 900,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .recommendationValue
                                    }
                                  </Typography>
                                </Box>

                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: '#64748b',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .deltaLabel
                                    }
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#cbd5e1',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {
                                      optimizationDecisionComparison
                                        .deltaValue
                                    }
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>
                          )}

                        {historyQuery.isFetching && (
                          <LinearProgress
                            sx={{
                              height: 2,

                              bgcolor:
                                'rgba(56,189,248,0.05)',
                            }}
                          />
                        )}

                        {historyQuery.isError && (
                          <Alert
                            severity="info"
                            sx={{
                              m: 1.5,
                            }}
                          >
                            No se pudo cargar el histórico completo
                            de esta relación.
                          </Alert>
                        )}

                        {historyContext
                          && historyContext.status
                            === 'ok'
                          && (
                            <>
                              <Box
                                sx={{
                                  px: {
                                    xs: 1,
                                    md: 1.5,
                                  },

                                  pt: 1.5,

                                  width: '100%',
                                  height: 300,
                                }}
                              >
                                <ResponsiveContainer>
                                  <ComposedChart
                                    data={
                                      optimizationHistoricalChartData
                                    }
                                  >
                                    <CartesianGrid
                                      stroke="rgba(148,163,184,0.12)"
                                      vertical={
                                        false
                                      }
                                    />

                                    <XAxis
                                      dataKey="label"
                                      tick={{
                                        fill:
                                          '#94a3b8',

                                        fontSize:
                                          11,
                                      }}
                                      minTickGap={
                                        24
                                      }
                                    />

                                    <YAxis
                                      yAxisId="primary"
                                      orientation="left"
                                      tick={{
                                        fill:
                                          '#94a3b8',

                                        fontSize:
                                          11,
                                      }}
                                      width={84}
                                      tickFormatter={(
                                        value,
                                      ) =>
                                        formatChartMetricValue(
                                          Number(
                                            value,
                                          ),
                                          chartMetric,
                                        )
                                      }
                                    />

                                    {chartMetric
                                      === 'margin_percentage'
                                      && (
                                        <YAxis
                                          yAxisId="units"
                                          orientation="right"
                                          tick={{
                                            fill:
                                              '#64748b',

                                            fontSize:
                                              10,
                                          }}
                                          width={72}
                                          tickFormatter={(
                                            value,
                                          ) =>
                                            formatNumber(
                                              Number(
                                                value,
                                              ),
                                            )
                                          }
                                        />
                                      )}

                                    <RechartsTooltip
                                      formatter={(
                                        value,
                                        name,
                                      ) => {
                                        if (
                                          name
                                          === 'Unidades'
                                        ) {
                                          return [
                                            formatNumber(
                                              Number(
                                                value,
                                              ),
                                            ),
                                            'Unidades',
                                          ];
                                        }

                                        return [
                                          formatChartMetricValue(
                                            Number(
                                              value,
                                            ),
                                            chartMetric,
                                          ),
                                          'Observado',
                                        ];
                                      }}
                                    />

                                    {chartMetric
                                      === 'margin_percentage'
                                      && optimizationResult
                                        .recommendation
                                        .suggested_margin_pct
                                        !== null
                                      && (
                                        <ReferenceLine
                                          yAxisId="primary"
                                          y={
                                            optimizationResult
                                              .recommendation
                                              .suggested_margin_pct
                                          }
                                          stroke="#fbbf24"
                                          strokeWidth={1.5}
                                          strokeDasharray="6 5"
                                          ifOverflow="extendDomain"
                                        />
                                      )}

                                    {chartMetric
                                      === 'average_net_unit_price'
                                      && optimizationResult
                                        .recommendation
                                        .suggested_price
                                        !== null
                                      && (
                                        <ReferenceLine
                                          yAxisId="primary"
                                          y={
                                            optimizationResult
                                              .recommendation
                                              .suggested_price
                                          }
                                          stroke="#fbbf24"
                                          strokeWidth={1.5}
                                          strokeDasharray="6 5"
                                          ifOverflow="extendDomain"
                                        />
                                      )}

                                    {chartMetric
                                      === 'margin_percentage'
                                      && (
                                        <Bar
                                          yAxisId="units"
                                          dataKey="units"
                                          name="Unidades"
                                          fill="#64748b"
                                          fillOpacity={0.24}
                                          barSize={14}
                                        />
                                      )}

                                    <Line
                                      yAxisId="primary"
                                      type="monotone"
                                      dataKey="historical"
                                      name="Observado"
                                      stroke="#38bdf8"
                                      strokeWidth={
                                        optimizationUsesActivityObservations
                                          ? 0
                                          : 2
                                      }
                                      dot={
                                        optimizationUsesActivityObservations
                                          ? {
                                            r: 4,
                                            fill: '#38bdf8',
                                            stroke: '#08111f',
                                            strokeWidth: 1.5,
                                          }
                                          : false
                                      }
                                      activeDot={{
                                        r: 5,
                                      }}
                                      connectNulls={
                                        false
                                      }
                                    />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </Box>

                              <Divider />

                              <Box
                                sx={{
                                  px: {
                                    xs: 1.5,
                                    md: 1.75,
                                  },

                                  py: 1.2,
                                }}
                              >
                                <Typography
                                  variant="overline"
                                  sx={{
                                    color: '#94a3b8',
                                    fontWeight: 850,
                                    letterSpacing: 0.7,
                                  }}
                                >
                                  {
                                    optimizationUsesActivityObservations
                                      ? 'Últimos periodos con actividad'
                                      : 'Evolución agregada por periodo'
                                  }
                                </Typography>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    mt: 0.2,
                                    color: '#64748b',
                                  }}
                                >
                                  {
                                    optimizationUsesActivityObservations
                                      ? (
                                        'Se muestran únicamente periodos '
                                        + 'con evidencia comercial para '
                                        + 'precio, coste o margen.'
                                      )
                                      : (
                                        'Permite observar conjuntamente '
                                        + 'volumen, actividad y evolución '
                                        + 'comercial por periodo.'
                                      )
                                  }
                                </Typography>
                              </Box>

                              <Divider />

                              <TableContainer
                                sx={{
                                  maxHeight: 340,
                                }}
                              >
                                <Table
                                  stickyHeader
                                  size="small"
                                  sx={{
                                    minWidth: 860,

                                    '& .MuiTableCell-root': {
                                      py: 0.85,

                                      borderColor:
                                        'rgba(148,163,184,0.09)',

                                      color:
                                        '#cbd5e1',
                                    },

                                    '& .MuiTableCell-head': {
                                      bgcolor:
                                        '#0b1525',

                                      color:
                                        '#64748b',

                                      fontSize:
                                        11,

                                      fontWeight:
                                        800,
                                    },
                                  }}
                                >
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>
                                        Periodo
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Compras
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Unidades
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Precio medio
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Margen %
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Ingresos
                                      </TableCell>

                                      <TableCell
                                        align="right"
                                      >
                                        Cobertura coste
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>

                                  <TableBody>
                                    {optimizationHistoricalTableRows
                                      .map(
                                        (
                                          point,
                                        ) => (
                                          <TableRow
                                            key={
                                              point
                                                .bucket_index
                                            }
                                            hover
                                          >
                                            <TableCell>
                                              {
                                                formatHistoricalBucketLabel(
                                                  point
                                                    .covered_start,

                                                  historySummary
                                                    ?.effective_time_grain,
                                                )
                                              }

                                              {point
                                                .is_partial_bucket
                                                && (
                                                  <Typography
                                                    component="span"
                                                    variant="caption"
                                                    sx={{
                                                      ml: 0.6,
                                                      color:
                                                        '#fbbf24',
                                                    }}
                                                  >
                                                    parcial
                                                  </Typography>
                                                )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatNumber(
                                                point
                                                  .transaction_count,
                                              )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatNumber(
                                                point
                                                  .units,
                                              )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatCurrency(
                                                point
                                                  .average_net_unit_price,
                                              )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatPct(
                                                point
                                                  .margin_percentage,
                                              )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatCurrency(
                                                point
                                                  .revenue,
                                              )}
                                            </TableCell>

                                            <TableCell
                                              align="right"
                                            >
                                              {formatPct(
                                                point
                                                  .cost_coverage_pct,
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ),
                                      )}
                                  </TableBody>
                                </Table>
                              </TableContainer>

                              <Box
                                sx={{
                                  px: {
                                    xs: 1.5,
                                    md: 1.75,
                                  },

                                  py: 1.1,

                                  borderTop:
                                    '1px solid rgba(148,163,184,0.10)',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#64748b',
                                    lineHeight: 1.5,
                                  }}
                                >
                                  Las operaciones individuales pueden
                                  revisarse desde “Ver últimas operaciones”.
                                  La coexistencia de cambios en precio,
                                  volumen o margen no implica por sí sola
                                  causalidad ni una elasticidad estimada.
                                </Typography>
                              </Box>
                            </>
                          )}
                      </Paper>
                    )}

                    <Box
                      sx={{
                        mt: 1.25,
                      }}
                    >
                      <CustomerRelationshipBenchmarkPanel
                        customerId={
                          customerId
                        }
                        productId={
                          productId ?? ''
                        }
                        startDate={
                          startDate
                        }
                        endDate={
                          endDate
                        }
                      />
                    </Box>

                    {optimizationResult
                      .scenarios
                      .pricing_matrix
                      && optimizationResult
                        .scenarios
                        .pricing_matrix
                        .length > 0 && (
                        <Paper
                          elevation={0}
                          sx={{
                            mt: 1.25,
                            borderRadius: 2,
                            border:
                              '1px solid rgba(148,163,184,0.14)',
                            bgcolor:
                              'rgba(15,23,42,0.30)',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              px: 1.5,
                              py: 1.1,
                            }}
                          >
                            <Typography
                              variant="overline"
                              sx={{
                                color: '#94a3b8',
                                fontWeight: 850,
                                letterSpacing: 0.7,
                              }}
                            >
                              Matriz de negociación · volumen
                            </Typography>
                          </Box>

                          <Divider />

                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    Cantidad
                                  </TableCell>

                                  <TableCell align="right">
                                    Precio objetivo
                                  </TableCell>

                                  <TableCell align="right">
                                    Descuento
                                  </TableCell>

                                  <TableCell align="right">
                                    Margen unitario
                                  </TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {optimizationResult
                                  .scenarios
                                  .pricing_matrix
                                  .map((tier) => (
                                    <TableRow
                                      key={
                                        `${tier.quantity}-${tier.suggested_price}`
                                      }
                                    >
                                      <TableCell>
                                        {formatNumber(
                                          tier.quantity,
                                        )}
                                      </TableCell>

                                      <TableCell align="right">
                                        {formatCurrency(
                                          tier.suggested_price,
                                        )}
                                      </TableCell>

                                      <TableCell align="right">
                                        {formatPct(
                                          tier.suggested_discount
                                          * 100,
                                        )}
                                      </TableCell>

                                      <TableCell align="right">
                                        {formatCurrency(
                                          tier.unit_margin,
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      )}
                  </Box>
                  <Divider />

                  <Box
                    sx={{
                      px: {
                        xs: 1.5,
                        md: 1.75,
                      },
                      py: 1.5,
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 850,
                        letterSpacing: 0.7,
                      }}
                    >
                      Calidad de la recomendación
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md:
                            'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 1,
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.4,
                          borderRadius: 2,
                          border:
                            '1px solid rgba(148,163,184,0.14)',
                          bgcolor:
                            'rgba(15,23,42,0.30)',
                        }}
                      >
                        <DecisionConfidenceScale
                          value={
                            optimizationResult
                              .recommendation
                              .confidence_label
                          }
                        />
                      </Paper>

                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.4,
                          borderRadius: 2,
                          border:
                            '1px solid rgba(148,163,184,0.14)',
                          bgcolor:
                            'rgba(15,23,42,0.30)',
                        }}
                      >
                        <DecisionRiskScale
                          score={
                            optimizationResult
                              .risks
                              .score
                          }
                          label={
                            optimizationResult
                              .risks
                              .label
                          }
                        />
                      </Paper>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      px: {
                        xs: 1.75,
                        md: 2,
                      },
                      py: 1.35,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#64748b',
                        fontWeight: 750,
                      }}
                    >
                      PROCEDENCIA DEL MODELO Y LOS DATOS
                    </Typography>

                    <Stack
                      direction={{
                        xs: 'column',
                        md: 'row',
                      }}
                      spacing={{
                        xs: 0.4,
                        md: 2.5,
                      }}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{ mt: 0.55 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8' }}
                      >
                        Familia:{' '}
                        {optimizationResult
                          .provenance
                          ?.model_family ?? '—'}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8' }}
                      >
                        Datos hasta:{' '}
                        {formatDate(
                          optimizationResult
                            .provenance
                            ?.data_cutoff_at,
                        )}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: '#94a3b8' }}
                      >
                        Entrenamiento:{' '}
                        {formatDate(
                          optimizationResult
                            .provenance
                            ?.training_finished_at,
                        )}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.65,
                        color: '#475569',
                        lineHeight: 1.5,
                      }}
                    >
                      Estas fechas pertenecen al optimizador.
                      No representan el periodo comercial que
                      estás analizando en el Centro de Decisión.
                    </Typography>
                  </Box>
                </Paper>
              )}

              {optimizationResult
                && optimizationResult.risks.review_mode
                  !== 'evaluation_possible'
                && (
                  <Alert
                    severity={
                      optimizationResult.risks.review_mode
                        === 'reference_only'
                        ? 'warning'
                        : 'info'
                    }
                  >
                    {optimizationResult.risks.review_mode
                      === 'reference_only'
                      ? (
                          'El resultado debe utilizarse únicamente '
                          + 'como referencia comercial.'
                        )
                      : (
                          'La recomendación requiere revisión '
                          + 'comercial antes de cualquier decisión.'
                        )}
                  </Alert>
                )}

              {coverageNeedsAttention && (
                <Alert severity="info">
                  La cobertura de coste de la relación es del{' '}
                  {formatPct(
                    coverage.current.cost_coverage_pct,
                  )}
                  . La optimización deberá interpretarse teniendo
                  en cuenta esta cobertura.
                </Alert>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.55,
                }}
              >
                El periodo seleccionado describe el contexto
                comercial observado. La ventana de datos y el
                modelo utilizados por el optimizador se mostrarán
                por separado cuando exista un resultado.
              </Typography>
            </Stack>
          )}

        {workspaceMode === 'analysis'
          && context
          && current
          && positioning
          && currentPositioning
          && coverage
          && (
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2.5,
                  border:
                    '1px solid rgba(56,189,248,0.18)',
                  background:
                    'linear-gradient(135deg, rgba(14,165,233,0.075) 0%, rgba(15,23,42,0.52) 38%, rgba(99,102,241,0.035) 100%)',
                  boxShadow:
                    '0 14px 38px rgba(2,6,23,0.22), inset 0 1px 0 rgba(255,255,255,0.025)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(6, minmax(0, 1fr))',
                    },
                  }}
                >
                  <CompactMetric
                    label="Ingresos de la relación"
                    value={formatCurrency(
                      current.revenue,
                    )}
                    comparison={context.comparison
                      ? formatCurrency(context.comparison.revenue)
                      : undefined}
                    change={context.comparison
                      ? formatSignedPct(changes?.revenue_delta_pct)
                      : undefined}
                    changeValue={changes?.revenue_delta_pct}
                  />

                  <CompactMetric
                    label="Margen sobre ingresos resueltos"
                    value={formatPct(
                      current.margin_percentage,
                    )}
                    comparison={context.comparison
                      ? formatPct(context.comparison.margin_percentage)
                      : undefined}
                    change={context.comparison
                      ? formatSignedPoints(changes?.margin_percentage_delta_points)
                      : undefined}
                    changeValue={changes?.margin_percentage_delta_points}
                  />

                  <CompactMetric
                    label="Cantidad vendida"
                    value={formatNumber(
                      current.units,
                    )}
                    comparison={context.comparison
                      ? formatNumber(context.comparison.units)
                      : undefined}
                    change={context.comparison
                      ? formatSignedPct(changes?.units_delta_pct)
                      : undefined}
                    changeValue={changes?.units_delta_pct}
                  />

                  <CompactMetric
                    label="Precio neto medio por unidad"
                    value={formatCurrency(
                      current.average_net_unit_price,
                    )}
                    comparison={context.comparison
                      ? formatCurrency(context.comparison.average_net_unit_price)
                      : undefined}
                    change={context.comparison
                      ? formatSignedPct(changes?.average_net_unit_price_delta_pct)
                      : undefined}
                    changeValue={changes?.average_net_unit_price_delta_pct}
                  />

                  <CompactMetric
                    label="Coste medio por unidad"
                    value={formatCurrency(
                      current.average_unit_cost,
                    )}
                    comparison={context.comparison
                      ? formatCurrency(context.comparison.average_unit_cost)
                      : undefined}
                    change={context.comparison
                      ? formatSignedPct(changes?.average_unit_cost_delta_pct)
                      : undefined}
                    changeValue={changes?.average_unit_cost_delta_pct}
                  />

                  <CompactMetric
                    label="Peso del producto en el cliente"
                    value={formatPct(
                      currentPositioning
                        .relationship_share_of_customer_revenue_pct,
                    )}
                    comparison={positioning.comparison
                      ? formatPct(
                          positioning.comparison
                            .relationship_share_of_customer_revenue_pct,
                        )
                      : undefined}
                    change={positioning.comparison
                      ? formatSignedPoints(
                          positioning.changes?.share_of_customer_delta_points,
                        )
                      : undefined}
                    changeValue={positioning.changes?.share_of_customer_delta_points}
                  />
                </Box>


              </Paper>

              {coverageNeedsAttention && (
                <Alert severity="info">
                  La cobertura de coste de la relación es del{' '}
                  {formatPct(
                    coverage.current.cost_coverage_pct,
                  )}
                  . Las métricas financieras mantienen fuera del
                  cálculo los costes no resueltos.
                </Alert>
              )}

              <CustomerRelationshipEconomicReadings
                changes={changes}
                coverage={coverage}
                current={current}
                comparison={context.comparison}
                selection={context.selection}
                series={context.series}
                recentTransactions={context.recent_transactions}
                currencyCode={DISPLAY_CURRENCY}
              />

              <CustomerRelationshipBenchmarkPanel
                customerId={customerId}
                productId={productId ?? ''}
                startDate={startDate}
                endDate={endDate}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 7fr) minmax(320px, 3fr)',
                  },
                  gap: 2,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.75,
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(56,189,248,0.16)',
                    background:
                      'linear-gradient(180deg, rgba(15,23,42,0.70) 0%, rgba(8,17,31,0.86) 100%)',
                    boxShadow:
                      '0 14px 34px rgba(2,6,23,0.18)',
                    minWidth: 0,
                  }}
                >
                  <Stack
                    spacing={1}
                  >
                    <Stack
                      direction={{
                        xs: 'column',
                        md: 'row',
                      }}
                      spacing={1.25}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'stretch',
                        md: 'center',
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 850,
                          }}
                        >
                          {chartTitle}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: '#64748b' }}
                        >
                          {chartSubtitle}
                        </Typography>
                      </Box>

                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={timelineMode}
                        onChange={(
                          _,
                          nextMode:
                            TimelineMode | null,
                        ) => {
                          if (nextMode) {
                            setTimelineMode(
                              nextMode,
                            );
                          }
                        }}
                        sx={{
                          flexShrink: 0,
                          '& .MuiToggleButton-root': {
                            px: 1.15,
                            py: 0.45,
                            textTransform: 'none',
                            color: '#94a3b8',
                            borderColor:
                              'rgba(148,163,184,0.18)',
                            '&.Mui-selected': {
                              color: '#f8fafc',
                              bgcolor:
                                'rgba(56,189,248,0.12)',
                            },
                          },
                        }}
                      >
                        <ToggleButton value="analysis">
                          Periodo analizado
                        </ToggleButton>

                        <ToggleButton value="history">
                          Histórico completo
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Stack>

                    <Stack
                      direction={{
                        xs: 'column',
                        sm: 'row',
                      }}
                      spacing={0.75}
                      justifyContent="space-between"
                      alignItems={{
                        xs: 'stretch',
                        sm: 'center',
                      }}
                    >
                      {timelineMode === 'history' ? (
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={historicalTimeGrain}
                          onChange={(
                            _,
                            nextTimeGrain:
                              MarginCenterHistoricalTimeGrain
                              | null,
                          ) => {
                            if (nextTimeGrain) {
                              setHistoricalTimeGrain(
                                nextTimeGrain,
                              );
                            }
                          }}
                          sx={{
                            flexWrap: 'wrap',
                            '& .MuiToggleButton-root': {
                              px: 1,
                              py: 0.35,
                              textTransform: 'none',
                              color: '#64748b',
                              borderColor:
                                'rgba(148,163,184,0.14)',
                              '&.Mui-selected': {
                                color: '#cbd5e1',
                                bgcolor:
                                  'rgba(148,163,184,0.08)',
                              },
                            },
                          }}
                        >
                          <ToggleButton value="auto">
                            Auto
                          </ToggleButton>

                          <ToggleButton value="day">
                            Día
                          </ToggleButton>

                          <ToggleButton value="week">
                            Semana
                          </ToggleButton>

                          <ToggleButton value="month">
                            Mes
                          </ToggleButton>
                        </ToggleButtonGroup>
                      ) : (
                        <Box />
                      )}

                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={chartMetric}
                        onChange={(
                          _,
                          nextMetric:
                            ChartMetric | null,
                        ) => {
                          if (nextMetric) {
                            setChartMetric(
                              nextMetric,
                            );
                          }
                        }}
                        sx={{
                          flexWrap: 'wrap',
                          '& .MuiToggleButton-root': {
                            px: 1.1,
                            py: 0.45,
                            textTransform: 'none',
                            color: '#94a3b8',
                            borderColor:
                              'rgba(148,163,184,0.18)',
                            '&.Mui-selected': {
                              color: '#f8fafc',
                              bgcolor:
                                'rgba(56,189,248,0.12)',
                            },
                          },
                        }}
                      >
                        {CHART_METRICS.map(
                          (metric) => (
                            <ToggleButton
                              key={metric.value}
                              value={metric.value}
                            >
                              {metric.label}
                            </ToggleButton>
                          ),
                        )}
                      </ToggleButtonGroup>
                    </Stack>
                  </Stack>

                  {timelineMode === 'history'
                    && historyQuery.isFetching
                    && (
                      <LinearProgress
                        sx={{
                          mt: 1.25,
                          height: 2,
                          bgcolor:
                            'rgba(56,189,248,0.05)',
                        }}
                      />
                    )}

                  {timelineMode === 'history'
                    && historyQuery.isError
                    && (
                      <Alert
                        severity="info"
                        sx={{ mt: 1.25 }}
                      >
                        No se pudo cargar el histórico
                        completo de esta relación.
                      </Alert>
                    )}

                  {timelineMode === 'history'
                    && historyContext
                    && historyContext.status !== 'ok'
                    && !historyQuery.isError
                    && (
                      <Alert
                        severity="info"
                        sx={{ mt: 1.25 }}
                      >
                        Esta relación no dispone de
                        histórico observado para
                        representar.
                      </Alert>
                    )}

                  <Box
                    sx={{
                      mt: 1.5,
                      width: '100%',
                      height: 300,
                    }}
                  >
                    <ResponsiveContainer>
                      <LineChart data={chartData}>
                        <CartesianGrid
                          stroke="rgba(148,163,184,0.12)"
                          vertical={false}
                        />

                        <XAxis
                          dataKey="label"
                          tick={{
                            fill: '#94a3b8',
                            fontSize: 11,
                          }}
                          minTickGap={24}
                        />

                        <YAxis
                          tick={{
                            fill: '#94a3b8',
                            fontSize: 11,
                          }}
                          width={96}
                          tickFormatter={(value) =>
                            formatChartMetricValue(
                              Number(value),
                              chartMetric,
                            )
                          }
                        />

                        <RechartsTooltip
                          formatter={(value) =>
                            formatChartMetricValue(
                              Number(value),
                              chartMetric,
                            )
                          }
                        />

                        {timelineMode === 'analysis' ? (
                          <>
                            <Line
                              type="monotone"
                              dataKey="current"
                              name="Periodo analizado"
                              stroke="#38bdf8"
                              strokeWidth={2}
                              dot={false}
                              connectNulls={false}
                            />

                            {comparisonMode !== 'none' && (
                              <Line
                                type="monotone"
                                dataKey="comparison"
                                name="Periodo comparado"
                                stroke="#94a3b8"
                                strokeWidth={1.5}
                                strokeDasharray="5 5"
                                dot={false}
                                connectNulls={false}
                              />
                            )}
                          </>
                        ) : (
                          <Line
                            type="monotone"
                            dataKey="historical"
                            name="Histórico completo"
                            stroke="#38bdf8"
                            strokeWidth={2}
                            dot={false}
                            connectNulls={false}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>

                <RelationshipCommercialQueryPanel
                  customerId={customerId}
                  productId={productId ?? ''}
                  customerLabel={context.customer?.label ?? customerId}
                  productLabel={context.product?.label ?? productId ?? ''}
                  selection={context.selection}
                  series={context.series}
                  currencyCode={DISPLAY_CURRENCY}
                />
              </Box>

              <CustomerRelationshipPositionPanel
                positioning={positioning}
                currencyCode={DISPLAY_CURRENCY}
                customerId={customerId}
                productId={productId ?? ''}
                customerLabel={context.customer?.label ?? customerId}
                productLabel={context.product?.label ?? productId ?? ''}
                selection={context.selection}
              />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 1.45fr) minmax(390px, 0.85fr)',
                  },
                  gap: 2,
                }}
              >
                <CustomerRelationshipActivityPanel
                  customerId={customerId}
                  productId={productId ?? ''}
                  startDate={startDate}
                  endDate={endDate}
                  comparisonMode={comparisonMode}
                  comparisonStartDate={comparisonStartDate}
                  comparisonEndDate={comparisonEndDate}
                />

                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border:
                      '1px solid rgba(148,163,184,0.14)',
                    background:
                      'linear-gradient(180deg, rgba(15,23,42,0.58) 0%, rgba(8,17,31,0.78) 100%)',
                    overflow: 'hidden',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      px: 1.6,
                      py: 1.3,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      alignItems="baseline"
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 850,
                        }}
                      >
                        Operaciones recientes
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: '#64748b' }}
                      >
                        {recentTransactions.length} / {RECENT_OPERATIONS_LIMIT} máx.
                      </Typography>
                    </Stack>

                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.2,
                        color: '#64748b',
                      }}
                    >
                      Últimas operaciones hasta el final del periodo. Pulsa una fila para ver precio, margen y cobertura.
                    </Typography>
                  </Box>

                  <Divider />

                  {recentTransactions.length > 0 && (
                    <Box sx={{ px: 1.3, py: 1, borderBottom: '1px solid rgba(148,163,184,0.10)' }}>
                      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                        {(['all', 'current', 'comparison', 'history'] as RecentPeriodFilter[])
                          .filter((filter) => filter !== 'comparison' || context.selection.comparison_mode !== 'none')
                          .map((filter) => (
                            <Button
                              key={filter}
                              size="small"
                              variant={recentPeriodFilter === filter ? 'contained' : 'outlined'}
                              aria-pressed={recentPeriodFilter === filter}
                              onClick={() => {
                                setRecentPeriodFilter(filter);
                                setExpandedTransactionId(null);
                              }}
                              sx={{
                                minWidth: 0, px: 0.8, py: 0.35, fontSize: 11,
                                textTransform: 'none', borderRadius: 1.2,
                                borderColor: 'rgba(148,163,184,0.22)',
                              }}
                            >
                              {RECENT_PERIOD_LABELS[filter]} · {recentCounts[filter]}
                            </Button>
                          ))}
                      </Stack>
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.55 }}>
                        Los filtros clasifican esta muestra de hasta 20 operaciones; no representan el total de cada periodo.
                      </Typography>
                    </Box>
                  )}

                  {recentTransactions.length === 0 ? (
                    <Box
                      sx={{
                        px: 2,
                        py: 4,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#94a3b8' }}
                      >
                        No existe histórico transaccional para
                        esta combinación.
                      </Typography>
                    </Box>
                  ) : visibleRecentGroups.length === 0 ? (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        No hay operaciones de {RECENT_PERIOD_LABELS[recentPeriodFilter].toLowerCase()} en esta muestra.
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ maxHeight: 440, overflowY: 'auto', overflowX: 'auto' }}>
                      <Table
                        stickyHeader
                        size="small"
                        sx={{
                          minWidth: 390,
                          '& .MuiTableCell-root': {
                            py: 0.9,
                            borderColor:
                              'rgba(148,163,184,0.09)',
                            color: '#cbd5e1',
                          },
                        }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              Fecha
                            </TableCell>

                            <TableCell align="right">
                              Cantidad
                            </TableCell>

                            <TableCell align="right">
                              Ingresos
                            </TableCell>

                            <TableCell
                              padding="checkbox"
                              aria-label="Detalle"
                            />
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {visibleRecentGroups.map(
                            ({ transaction, group }) => {
                              const periodColor = group === 'current'
                                ? '#34d399' : group === 'comparison' ? '#7dd3fc' : '#94a3b8';
                              const periodLabel = RECENT_PERIOD_LABELS[group];
                              const expanded =
                                expandedTransactionId
                                === transaction.transaction_id;

                              return (
                                <Fragment
                                  key={transaction.transaction_id}
                                >
                                  <TableRow
                                    key={transaction.transaction_id}
                                    hover
                                    onClick={() =>
                                      setExpandedTransactionId(
                                        (currentId) =>
                                          currentId
                                            === transaction.transaction_id
                                            ? null
                                            : transaction.transaction_id,
                                      )
                                    }
                                    sx={{
                                      cursor: 'pointer',
                                      '&:hover': {
                                        bgcolor:
                                          'rgba(56,189,248,0.045)',
                                      },
                                    }}
                                  >
                                    <TableCell>
                                      <Stack spacing={0.15} alignItems="flex-start">
                                        <Typography variant="body2" sx={{ color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                                          {formatDate(transaction.transaction_date)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: periodColor, fontSize: 10, fontWeight: 750, lineHeight: 1.2 }}>
                                          {periodLabel}
                                        </Typography>
                                      </Stack>
                                    </TableCell>

                                    <TableCell align="right">
                                      {formatNumber(
                                        transaction.quantity,
                                      )}
                                    </TableCell>

                                    <TableCell align="right">
                                      {formatCurrency(
                                        transaction.revenue,
                                      )}
                                    </TableCell>

                                    <TableCell padding="checkbox">
                                      <IconButton
                                        size="small"
                                        aria-label={
                                          expanded
                                            ? 'Ocultar detalle'
                                            : 'Ver detalle'
                                        }
                                        onClick={(event) => {
                                          event.stopPropagation();

                                          setExpandedTransactionId(
                                            (currentId) =>
                                              currentId
                                                === transaction
                                                  .transaction_id
                                                ? null
                                                : transaction
                                                  .transaction_id,
                                          );
                                        }}
                                        sx={{
                                          color: '#64748b',
                                        }}
                                      >
                                        {expanded ? (
                                          <KeyboardArrowDownRoundedIcon
                                            fontSize="small"
                                          />
                                        ) : (
                                          <KeyboardArrowRightRoundedIcon
                                            fontSize="small"
                                          />
                                        )}
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>

                                  <TableRow
                                    key={
                                      `${transaction.transaction_id}-detail`
                                    }
                                  >
                                    <TableCell
                                      colSpan={4}
                                      sx={{
                                        py: '0 !important',
                                        bgcolor:
                                          'rgba(15,23,42,0.42)',
                                      }}
                                    >
                                      <Collapse
                                        in={expanded}
                                        timeout="auto"
                                        unmountOnExit
                                      >
                                        <Box
                                          sx={{
                                            px: 1,
                                            py: 1.25,
                                            display: 'grid',
                                            gridTemplateColumns: {
                                              xs: '1fr',
                                              sm:
                                                'repeat(3, minmax(0, 1fr))',
                                            },
                                            gap: 1.25,
                                          }}
                                        >
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#64748b',
                                              }}
                                            >
                                              Precio neto por unidad
                                            </Typography>

                                            <Typography
                                              variant="body2"
                                              sx={{
                                                mt: 0.15,
                                                color: '#f8fafc',
                                                fontWeight: 750,
                                              }}
                                            >
                                              {formatCurrency(
                                                transaction
                                                  .net_unit_price,
                                              )}
                                            </Typography>
                                          </Box>

                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#64748b',
                                              }}
                                            >
                                              Margen sobre ingresos
                                            </Typography>

                                            <Typography
                                              variant="body2"
                                              sx={{
                                                mt: 0.15,
                                                color: '#f8fafc',
                                                fontWeight: 750,
                                              }}
                                            >
                                              {formatPct(
                                                transaction
                                                  .margin_percentage,
                                              )}
                                            </Typography>
                                          </Box>

                                          <Box>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: '#64748b',
                                              }}
                                            >
                                              Coste resuelto
                                            </Typography>

                                            <Typography
                                              variant="body2"
                                              sx={{
                                                mt: 0.15,
                                                color: '#f8fafc',
                                                fontWeight: 750,
                                              }}
                                            >
                                              {
                                                transaction.cost_resolved
                                                  ? 'Sí'
                                                  : 'No'
                                              }
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Collapse>
                                    </TableCell>
                                  </TableRow>
                                </Fragment>
                              );
                            },
                          )}
                        </TableBody>  
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.55,
                }}
              >
                Esta superficie presenta hechos agregados y
                evidencia transaccional observada. No implica por
                sí misma causalidad, riesgo, oportunidad ni
                recomendación.
              </Typography>
            </Stack>
          )}
      </DialogContent>
    </Dialog>
  );
}
